// src/pages/ProgressPaymentClaims.tsx
// Milestone-linked progress payment claims — full claim package assembly and lifecycle
// Spec: TrafficProcure Progress Payment Claims Design Brief v0.1

import { useState, useEffect, useCallback } from 'react'
import { useAuthStore } from '../stores/auth.store'
import {
  CLAIM_JOBS, MOCK_MILESTONES, CLAIM_STATUS_META,
  MILESTONE_STATUS_META, COMPONENT_STATUS_META,
  ClaimStatus, MilestoneStatus, ProgressClaim, Milestone, ClaimJob,
  getMilestonesForJob, getClaimsForJob, getActiveClaims,
  calcJobFinancials, countComponentsAccepted, hasFlaggedComponent,
  // Phase 2 live stores
  getLiveMilestones, getLiveClaims, getMilestonesForJob2, updateMilestone, upsertClaim,
} from '../utils/mockDataClaims'
import { computeMilestoneEligibility, type MilestoneEligibilityResult } from '../utils/workflowPipeline'
import { pullLiveEvidence, type LiveEvidenceItem } from '../utils/wiStepCompletion'
import {
  pullAndAssembleEvidence, toggleEvidenceItem, lockEvidencePack, getClaimEvidencePack,
  type ClaimEvidencePack, EVIDENCE_TYPE_META,
} from '../utils/liveClaim'
import { getXeroSync, recordXeroSync } from '../utils/mockDataProjectGaps'
import { buildDocumentHeader, buildDocumentFooter } from '../utils/documentBranding'
import { MOCK_VARIATIONS, MOCK_CONTRACTS } from '../utils/mockDataCommercial'
import { MOCK_SUBCONTRACTS, BCIPA_STATUS_META } from '../utils/mockDataSubcontracts'
import { AgentSidebar } from '../components/AgentSidebar'
import {
  DollarSign, FileText, Camera, Shield, CheckSquare, AlertTriangle,
  CheckCircle, Clock, ChevronRight, ChevronDown, X, Plus, Send,
  Download, RotateCcw, Lock, User, Building, ExternalLink,
  ArrowRight, Package, Clipboard, Star, TrendingUp, FileSearch,
  Eye, UploadCloud, Info, Circle, Zap, RefreshCw, ListChecks,} from 'lucide-react'

// ── Formatters ────────────────────────────────────────────────────

function fmtCurrency(n: number) {
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 0 }).format(n)
}
function fmtDate(iso?: string) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: '2-digit' })
}
function fmtDateTime(iso?: string) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
}

// ── Small shared components ───────────────────────────────────────

function ClaimStatusBadge({ status }: { status: ClaimStatus }) {
  const m = CLAIM_STATUS_META[status]
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${m.bg} ${m.text}`}>{m.label}</span>
}

function MsStatusBadge({ status }: { status: MilestoneStatus }) {
  const m = MILESTONE_STATUS_META[status]
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-full font-medium ${m.bg} ${m.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${m.dot}`} />
      {m.label}
    </span>
  )
}

function ComponentPip({ status, label }: { status: string; label: string }) {
  const colors: Record<string, string> = {
    PENDING:   'bg-gray-200 text-gray-400',
    POPULATED: 'bg-amber-200 text-amber-700',
    ACCEPTED:  'bg-green-200 text-green-700',
    FLAGGED:   'bg-red-200 text-red-700',
  }
  const icons: Record<string, string> = { PENDING: '○', POPULATED: '◑', ACCEPTED: '✓', FLAGGED: '!' }
  return (
    <div className="flex flex-col items-center gap-1">
      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${colors[status] ?? colors.PENDING}`}>
        {icons[status] ?? '○'}
      </div>
      <span className="text-[10px] text-gray-500 text-center leading-tight w-12">{label}</span>
    </div>
  )
}

function ProgressRing({ pct, size = 52 }: { pct: number; size?: number }) {
  const r = (size - 8) / 2
  const circ = 2 * Math.PI * r
  const dash = (pct / 100) * circ
  return (
    <svg width={size} height={size} className="-rotate-90">
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#E5E7EB" strokeWidth={5} />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={pct >= 80 ? '#16A34A' : '#2E75B6'} strokeWidth={5}
        strokeDasharray={`${dash} ${circ}`} strokeLinecap="round" />
    </svg>
  )
}

function SectionHeader({ icon: Icon, code, title, color = 'text-[#1F3864]' }: { icon: any; code: string; title: string; color?: string }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <div className="w-7 h-7 rounded-lg bg-[#1F3864] flex items-center justify-center flex-shrink-0">
        <Icon className="w-3.5 h-3.5 text-white" />
      </div>
      <div>
        <span className="text-xs font-mono text-gray-400 mr-2">{code}</span>
        <span className={`text-sm font-semibold ${color}`}>{title}</span>
      </div>
    </div>
  )
}

// ── Claim Detail Panel ────────────────────────────────────────────

function ClaimDetailPanel({
  claim, job, milestone, onClose, canSign, canSubmit, canRecordPayment,
}: {
  claim: ProgressClaim
  job: ClaimJob
  milestone: Milestone
  onClose: () => void
  canSign: boolean
  canSubmit: boolean
  canRecordPayment: boolean
}) {
  const { user } = useAuthStore()
  const [activeTab, setActiveTab] = useState<'FS' | 'EW' | 'SD' | 'FD' | 'QC'>('FS')
  // Phase 3: live evidence pack
  const [evidencePack, setEvidencePack] = useState<ClaimEvidencePack | null>(
    () => getClaimEvidencePack(milestone.id)
  )
  const [expandDispute, setExpandDispute] = useState(false)
  // PM-11: Xero sync state
  const [xeroSyncing, setXeroSyncing] = useState(false)
  const [xeroSync, setXeroSync] = useState(() => getXeroSync(claim.claimRef))
  const fs = claim.financialSummary

  // CM-07: compute adjusted contract value = original + approved variations for this job
  const projectContract = MOCK_CONTRACTS.find(c => c.jobId === claim.jobId)
  const approvedVarTotal = projectContract
    ? MOCK_VARIATIONS
        .filter(v => v.jobId === claim.jobId && v.status === 'APPROVED')
        .reduce((s, v) => s + (v.approvedAmount ?? 0), 0)
    : 0
  const adjustedContractValue = projectContract
    ? projectContract.originalContractValue + approvedVarTotal
    : job.contractValue

  const tabs: { key: 'FS'|'EW'|'SD'|'FD'|'QC'; label: string; icon: any; status: string }[] = [
    { key: 'FS', label: 'Financial Summary',      icon: DollarSign,   status: claim.componentFS },
    { key: 'EW', label: 'Evidence of Works',       icon: Camera,       status: claim.componentEW },
    { key: 'SD', label: 'Statutory Declaration',   icon: FileText,     status: claim.componentSD },
    { key: 'FD', label: 'Formal Documentation',    icon: Clipboard,    status: claim.componentFD },
    { key: 'QC', label: 'Quality Certifications',  icon: CheckSquare,  status: claim.componentQC },
  ]

  const unpaidSuppliers = claim.supplierPayments.filter(s => s.status === 'UNPAID')
  const missingCerts = claim.qualityCerts.filter(c => !c.attached)
  const includedPhotos = claim.evidencePhotos.filter(p => p.included)
  const acceptedCount = countComponentsAccepted(claim)
  const flagged = hasFlaggedComponent(claim)

  return (
    <div className="flex flex-col h-full">
      {/* Panel header */}
      <div className="px-5 py-4 border-b bg-gray-50">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className="text-xs font-mono text-gray-400">{claim.claimRef}</span>
              <ClaimStatusBadge status={claim.status} />
              {flagged && <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full font-medium">⚑ Action needed</span>}
            </div>
            <h2 className="text-sm font-semibold text-gray-900 leading-snug">{milestone.title}</h2>
            <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-500 flex-wrap">
              <span className="flex items-center gap-1"><Building className="w-3 h-3" />{job.clientName}</span>
              <span className="flex items-center gap-1"><User className="w-3 h-3" />{claim.createdByName}</span>
              {milestone.submissionDeadline && (
                <span className={`flex items-center gap-1 font-medium ${new Date(milestone.submissionDeadline) < new Date() ? 'text-red-600' : 'text-amber-600'}`}>
                  <Clock className="w-3 h-3" />Due {fmtDate(milestone.submissionDeadline)}
                </span>
              )}
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-lg">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Component progress strip */}
        <div className="flex items-center justify-between mt-3 bg-white border border-gray-200 rounded-xl px-4 py-2.5">
          {tabs.map((t, i) => (
            <div key={t.key} className="flex items-center gap-2">
              <ComponentPip status={t.status} label={t.key} />
              {i < tabs.length - 1 && <div className="w-4 h-px bg-gray-200 flex-shrink-0" />}
            </div>
          ))}
          <div className="ml-3 pl-3 border-l text-center">
            <div className="text-sm font-bold text-gray-900">{acceptedCount}/5</div>
            <div className="text-[10px] text-gray-400">accepted</div>
          </div>
        </div>
      </div>

      {/* Flagged alert */}
      {claim.internalNotes && (
        <div className="mx-5 mt-3 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl p-3">
          <Info className="w-3.5 h-3.5 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-800 leading-relaxed">{claim.internalNotes}</p>
        </div>
      )}

      {/* A13 CA Agent sidebar */}
      <AgentSidebar context={{
        page: 'claims',
        contracts:  MOCK_CONTRACTS,
        variations: MOCK_VARIATIONS,
        claims:     getLiveClaims(),
        platformDate: '2026-06-17',
      }} />

      {/* Tab strip */}
      <div className="flex border-b border-gray-200 mx-5 mt-3 overflow-x-auto">
        {tabs.map(t => {
          const statusColor = t.status === 'ACCEPTED' ? 'text-green-600' : t.status === 'FLAGGED' ? 'text-red-600' : t.status === 'POPULATED' ? 'text-amber-600' : 'text-gray-400'
          return (
            <button key={t.key} onClick={() => setActiveTab(t.key)}
              className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 -mb-px whitespace-nowrap transition-colors ${activeTab === t.key ? 'border-[#2E75B6] text-[#2E75B6]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
              <t.icon className={`w-3 h-3 ${activeTab === t.key ? 'text-[#2E75B6]' : statusColor}`} />
              {t.key}
              <span className={`text-[10px] font-semibold ${statusColor}`}>
                {t.status === 'ACCEPTED' ? '✓' : t.status === 'FLAGGED' ? '!' : t.status === 'POPULATED' ? '◑' : '○'}
              </span>
            </button>
          )
        })}
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">

        {/* ── FS: Financial Summary ── */}
        {activeTab === 'FS' && (
          <div className="space-y-4">
            <SectionHeader icon={DollarSign} code="FS" title="Financial Summary" />

            {/* Invoice header */}
            <div className="bg-[#1F3864] rounded-xl p-4 text-white">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-xs text-blue-300">Progress Payment Claim</div>
                  <div className="text-lg font-bold">{claim.claimRef}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-blue-300">Invoice</div>
                  <div className="text-sm font-mono">{fs.invoiceNumber}</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white/10 rounded-lg p-2.5">
                  <div className="text-xs text-blue-300">Milestone claim amount</div>
                  <div className="text-base font-bold">{fmtCurrency(fs.milestoneAmount)}</div>
                </div>
                <div className="bg-white/10 rounded-lg p-2.5">
                  <div className="text-xs text-blue-300">Less retention ({job.retentionPct}%)</div>
                  <div className="text-base font-bold text-amber-300">− {fmtCurrency(fs.retentionHeld)}</div>
                </div>
                <div className="bg-white/10 rounded-lg p-2.5">
                  <div className="text-xs text-blue-300">Net claimed (ex GST)</div>
                  <div className="text-base font-bold">{fmtCurrency(fs.netClaimed)}</div>
                </div>
                <div className="bg-green-500/20 rounded-lg p-2.5">
                  <div className="text-xs text-green-300">Total inc GST</div>
                  <div className="text-lg font-bold text-green-300">{fmtCurrency(fs.totalIncGST)}</div>
                </div>
              </div>
            </div>

            {/* Running totals */}
            <div className="grid grid-cols-3 gap-2">
              {[
                { label: 'Previous payments', value: fmtCurrency(fs.previousPayments), sub: 'received to date', color: 'text-gray-700' },
                { label: 'This claim (net)', value: fmtCurrency(fs.netClaimed), sub: 'ex GST ex retention', color: 'text-[#2E75B6]' },
                { label: 'Balance remaining', value: fmtCurrency(adjustedContractValue - fs.previousPayments - fs.netClaimed), sub: 'adjusted contract less claimed', color: 'text-gray-700' },
              ].map(({ label, value, sub, color }) => (
                <div key={label} className="bg-gray-50 border border-gray-200 rounded-xl p-3 text-center">
                  <div className={`text-sm font-bold ${color}`}>{value}</div>
                  <div className="text-[10px] text-gray-500 font-medium mt-0.5">{label}</div>
                  <div className="text-[10px] text-gray-400">{sub}</div>
                </div>
              ))}
            </div>

            {/* CM-07: Show variation uplift if approved variations exist */}
            {approvedVarTotal > 0 && (
              <div className="bg-green-50 border border-green-200 rounded-xl px-3 py-2.5 flex items-center gap-2 text-xs text-green-800">
                <span className="font-semibold">Adjusted contract value:</span>
                <span className="font-mono">{fmtCurrency(adjustedContractValue)}</span>
                <span className="text-green-600">(original {fmtCurrency(job.contractValue)} + {fmtCurrency(approvedVarTotal)} approved variations)</span>
              </div>
            )}

            {/* Line items */}
            <div>
              <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">Itemised expenditure</div>
              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-gray-50 border-b">
                      <th className="text-left px-3 py-2 font-medium text-gray-600">Description</th>
                      <th className="text-left px-3 py-2 font-medium text-gray-600">Supplier</th>
                      <th className="text-right px-3 py-2 font-medium text-gray-600">Amount</th>
                      <th className="text-left px-3 py-2 font-medium text-gray-600">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {fs.poLineItems.map((item, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-3 py-2 text-gray-800">{item.description}</td>
                        <td className="px-3 py-2 text-gray-500">{item.supplier}</td>
                        <td className="px-3 py-2 text-right font-mono text-gray-900">{fmtCurrency(item.amount)}</td>
                        <td className="px-3 py-2">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${item.status === 'DELIVERED' || item.status === 'PAID' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                            {item.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50 border-t">
                    <tr>
                      <td colSpan={2} className="px-3 py-2 text-xs font-semibold text-gray-700">Materials: {fmtCurrency(fs.materialsCostTotal)}  ·  Labour: {fmtCurrency(fs.labourCostTotal)}</td>
                      <td className="px-3 py-2 text-right text-xs font-bold text-gray-900">{fmtCurrency(fs.milestoneAmount)}</td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {/* Actions */}
            {(claim.componentFS === 'POPULATED' || claim.componentFS === 'FLAGGED') && (
              <div className="flex gap-2">
                <button className="flex-1 py-2 text-xs font-semibold bg-green-100 text-green-700 hover:bg-green-200 rounded-lg transition-colors flex items-center justify-center gap-1.5">
                  <CheckCircle className="w-3.5 h-3.5" />Accept financial summary
                </button>
                <button className="py-2 px-3 text-xs font-semibold bg-red-50 text-red-600 hover:bg-red-100 rounded-lg transition-colors">
                  Flag for revision
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── EW: Evidence of Works ── */}
        {activeTab === 'EW' && (
          <div className="space-y-4">
            <SectionHeader icon={Camera} code="EW" title="Evidence of Works" />
            {!evidencePack ? (
              <div className="border border-dashed border-indigo-300 rounded-xl p-6 text-center bg-indigo-50/50">
                <Camera className="w-8 h-8 text-indigo-300 mx-auto mb-2" />
                <div className="text-sm font-medium text-indigo-800 mb-1">Evidence not yet assembled</div>
                <div className="text-xs text-indigo-600 mb-4">
                  Pull real completion records from Work Instructions, hold point clearances, and GRN receipts.
                  {milestone.linkedWIs.length > 0 && <span className="block mt-1">Linked WIs: {milestone.linkedWIs.join(', ')}</span>}
                </div>
                <button onClick={() => {
                  const pack = pullAndAssembleEvidence(milestone.id, milestone.jobId, milestone.linkedWIs, milestone.wiIdMap ?? {}, user?.fullName ?? 'PM/PC')
                  setEvidencePack(pack)
                }} className="btn-primary text-sm flex items-center gap-2 mx-auto">
                  <Zap className="w-4 h-4" />Pull from field records
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className={`flex items-center justify-between px-3 py-2 rounded-lg text-xs ${evidencePack.locked ? 'bg-green-50 border border-green-200' : 'bg-indigo-50 border border-indigo-200'}`}>
                  <div className="flex items-center gap-2">
                    {evidencePack.locked
                      ? <><CheckCircle className="w-3.5 h-3.5 text-green-600" /><span className="text-green-800 font-medium">Evidence pack accepted — {evidencePack.includedItems} items included</span></>
                      : <><Info className="w-3.5 h-3.5 text-indigo-600" /><span className="text-indigo-800">{evidencePack.includedItems} of {evidencePack.totalItems} items included · Pulled {new Date(evidencePack.pulledAt).toLocaleDateString('en-AU')}</span></>}
                  </div>
                  {!evidencePack.locked && (
                    <div className="flex gap-2">
                      <button onClick={() => {
                        const pack = pullAndAssembleEvidence(milestone.id, milestone.jobId, milestone.linkedWIs, milestone.wiIdMap ?? {}, user?.fullName ?? 'PM/PC')
                        setEvidencePack(pack)
                      }} className="text-indigo-600 hover:underline flex items-center gap-1">
                        <RefreshCw className="w-3 h-3" />Refresh
                      </button>
                      <button onClick={() => { const u = lockEvidencePack(milestone.id); setEvidencePack(u) }} className="bg-green-600 text-white px-2 py-0.5 rounded text-xs font-medium hover:bg-green-700">
                        Accept pack
                      </button>
                    </div>
                  )}
                </div>
                {evidencePack.items.length === 0 ? (
                  <div className="text-center py-6 text-gray-400 text-sm">No field records found for the linked Work Instructions.</div>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
                    {evidencePack.items.map(item => {
                      const meta = EVIDENCE_TYPE_META[item.type as keyof typeof EVIDENCE_TYPE_META]
                      return (
                        <div key={item.id} className={`border rounded-xl p-3 transition-all ${item.included ? 'border-gray-200 bg-white' : 'border-gray-100 bg-gray-50 opacity-60'}`}>
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${meta.bg} ${meta.text}`}>{meta.icon} {meta.label}</span>
                                <span className="text-xs font-mono text-gray-400">{item.reference}</span>
                              </div>
                              <p className="text-xs font-medium text-gray-900">{item.title}</p>
                              <div className="flex items-center gap-3 mt-1 text-[10px] text-gray-400 flex-wrap">
                                <span className="flex items-center gap-1"><User className="w-2.5 h-2.5" />{item.completedByName}</span>
                                <span className="flex items-center gap-1"><Clock className="w-2.5 h-2.5" />{new Date(item.completedAt).toLocaleDateString('en-AU')}</span>
                                {item.photoCount > 0 && <span className="flex items-center gap-1"><Camera className="w-2.5 h-2.5" />{item.photoCount} photo{item.photoCount !== 1 ? 's' : ''}</span>}
                                {item.witnessName && <span>Witnessed: {item.witnessName}</span>}
                              </div>
                              {item.detail && <p className="text-[10px] text-gray-500 mt-0.5 italic">{item.detail}</p>}
                            </div>
                            {!evidencePack.locked && (
                              <button onClick={() => { const u = toggleEvidenceItem(milestone.id, item.id); setEvidencePack(u) }}
                                className={`text-[10px] px-2 py-0.5 rounded font-medium flex-shrink-0 ${item.included ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' : 'bg-gray-200 text-gray-500 hover:bg-gray-300'}`}>
                                {item.included ? '✓ Included' : 'Excluded'}
                              </button>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── SD: Statutory Declarations ── */}
        {activeTab === 'SD' && (
          <div className="space-y-4">
            <SectionHeader icon={FileText} code="SD" title="Statutory Declaration" />

            {unpaidSuppliers.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-2">
                <AlertTriangle className="w-3.5 h-3.5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="text-xs font-semibold text-red-800 mb-0.5">
                    {unpaidSuppliers.length} unpaid supplier{unpaidSuppliers.length !== 1 ? 's' : ''} — declaration cannot be signed until resolved
                  </div>
                  <div className="text-xs text-red-700">
                    {unpaidSuppliers.map(s => `${s.supplierName} (${s.poRef} — ${fmtCurrency(s.amount)})`).join(' · ')}
                  </div>
                </div>
              </div>
            )}

            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <div className="bg-gray-50 px-3 py-2 border-b">
                <div className="text-xs font-semibold text-gray-700">Supplier and subcontractor payment schedule</div>
                <div className="text-[10px] text-gray-500 mt-0.5">All parties associated with this milestone scope</div>
              </div>
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b">
                    <th className="text-left px-3 py-2 font-medium text-gray-600">Supplier / subcontractor</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-600">ABN</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-600">PO ref</th>
                    <th className="text-right px-3 py-2 font-medium text-gray-600">Amount</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-600">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {claim.supplierPayments.map(s => (
                    <tr key={s.supplierId} className={s.status === 'UNPAID' ? 'bg-red-50/50' : ''}>
                      <td className="px-3 py-2 text-gray-800 font-medium">{s.supplierName}</td>
                      <td className="px-3 py-2 font-mono text-gray-500">{s.abn}</td>
                      <td className="px-3 py-2 font-mono text-gray-500">{s.poRef}</td>
                      <td className="px-3 py-2 text-right font-mono text-gray-900">{fmtCurrency(s.amount)}</td>
                      <td className="px-3 py-2">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${s.status === 'PAID' ? 'bg-green-100 text-green-700' : s.status === 'UNPAID' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-500'}`}>
                          {s.status === 'PAID' ? `✓ Paid ${fmtDate(s.paidDate)}` : s.status === 'UNPAID' ? '⚑ Unpaid' : 'Not yet due'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Stat dec template */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="w-3.5 h-3.5 text-blue-700" />
                <span className="text-xs font-semibold text-blue-900">Statutory Declaration (QLD) — pre-filled template</span>
              </div>
              <p className="text-xs text-blue-800 leading-relaxed mb-3">
                I, <strong>{CLAIM_JOBS.find(j => j.id === claim.jobId)?.pmName ?? 'PM Name'}</strong>, of MCB Civil & Electrical Pty Ltd
                (ABN 44 128 993 087), do solemnly and sincerely declare that all subcontractors and suppliers listed in the
                payment schedule above have been paid in full for all work, services, and materials supplied in connection with
                the works described in Claim {claim.claimRef} — {milestone.title}, and I make this solemn declaration
                conscientiously believing the same to be true and by virtue of the provisions of the Oaths Act 1867 (QLD).
              </p>
              {claim.declarationSignedBy ? (
                <div className="flex items-center gap-2 bg-green-100 rounded-lg px-3 py-2 text-xs text-green-700">
                  <CheckCircle className="w-3.5 h-3.5" />
                  Signed by {claim.declarationSignedBy} on {fmtDateTime(claim.declarationSignedAt)} — witnessed by JP required before submission
                </div>
              ) : (
                <div className="flex gap-2">
                  <button
                    disabled={unpaidSuppliers.length > 0 || !canSign}
                    className="flex-1 py-2 text-xs font-semibold bg-[#1F3864] text-white hover:bg-[#2E75B6] rounded-lg flex items-center justify-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <Lock className="w-3.5 h-3.5" />
                    {canSign ? 'Sign declaration' : 'PM sign-off required'}
                  </button>
                  <button onClick={() => generateStatutoryDeclarationPDF(claim, job, milestone)} className="py-2 px-3 text-xs bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 rounded-lg flex items-center gap-1.5">
                    <Download className="w-3.5 h-3.5" />PDF
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── FD: Formal Documentation ── */}
        {activeTab === 'FD' && (
          <div className="space-y-4">
            <SectionHeader icon={Clipboard} code="FD" title="Formal Documentation" />

            <div className={`rounded-xl border p-4 ${job.clientType === 'TMR' ? 'bg-blue-50 border-blue-200' : job.clientType === 'ENERGEX' ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-200'}`}>
              <div className="text-xs font-semibold text-gray-700 mb-1">
                {job.clientType === 'TMR' ? '📋 TMR Milestone Claim Form (TP-MCF)' :
                 job.clientType === 'ENERGEX' ? '⚡ Payment Claim — SOPA QLD (Energex format)' :
                 '📋 Progress Payment Claim Form (Council format)'}
              </div>
              <div className="text-xs text-gray-600">Form type auto-detected from client: <strong>{job.clientName}</strong></div>
            </div>

            {/* Pre-filled form fields */}
            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <div className="bg-gray-50 px-3 py-2 border-b text-xs font-semibold text-gray-700">Auto-populated claim fields</div>
              <div className="divide-y divide-gray-50">
                {[
                  { label: 'Contract reference', value: job.contractRef },
                  { label: 'Project name', value: job.projectName },
                  { label: 'Claim reference', value: claim.claimRef },
                  { label: 'Invoice number', value: claim.financialSummary.invoiceNumber },
                  { label: 'Milestone reference', value: milestone.reference },
                  { label: 'Milestone description', value: milestone.title },
                  { label: 'Claimed amount (ex GST)', value: fmtCurrency(claim.financialSummary.netClaimed) },
                  { label: 'GST', value: fmtCurrency(claim.financialSummary.gstAmount) },
                  { label: 'Total claimed (inc GST)', value: fmtCurrency(claim.financialSummary.totalIncGST) },
                  { label: 'Cumulative claimed to date (ex GST)', value: fmtCurrency(claim.financialSummary.previousPayments + claim.financialSummary.netClaimed) },
                  { label: 'Client contact (approval)', value: milestone.clientContactName ?? '—' },
                  { label: 'Authorised PM', value: job.pmName },
                  { label: 'PM email', value: job.pmEmail },
                ].map(({ label, value }) => (
                  <div key={label} className="grid grid-cols-2 gap-2 px-3 py-2">
                    <span className="text-xs text-gray-500">{label}</span>
                    <span className="text-xs font-medium text-gray-900">{value}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800 flex items-start gap-2">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-600 flex-shrink-0 mt-0.5" />
              PM signature and date required on the generated form before submission. A wet signature is required for all TMR and Energex submissions.
            </div>

            {(claim.componentFD === 'POPULATED' || claim.componentFD === 'FLAGGED') && (
              <div className="flex gap-2">
                <button className="flex-1 py-2 text-xs font-semibold bg-green-100 text-green-700 hover:bg-green-200 rounded-lg flex items-center justify-center gap-1.5">
                  <CheckCircle className="w-3.5 h-3.5" />Accept formal documentation
                </button>
                <button onClick={() => generateClaimForm(claim, job, milestone)} className="py-2 px-3 text-xs bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 rounded-lg flex items-center gap-1.5">
                  <Download className="w-3.5 h-3.5" />Generate PDF
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── QC: Quality Certifications ── */}
        {activeTab === 'QC' && (
          <div className="space-y-4">
            <SectionHeader icon={CheckSquare} code="QC" title="Quality Certifications" />

            {missingCerts.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2">
                <AlertTriangle className="w-3.5 h-3.5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-amber-800">
                  <strong>{missingCerts.length} certificate{missingCerts.length !== 1 ? 's' : ''} not yet attached:</strong>{' '}
                  {missingCerts.map(c => c.title).join(' · ')}
                </div>
              </div>
            )}

            <div className="space-y-2">
              {claim.qualityCerts.map(cert => (
                <div key={cert.id} className={`border rounded-xl p-3 ${cert.attached ? 'border-gray-200 bg-white' : 'border-amber-200 bg-amber-50'}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                          cert.type === 'ITP_CLEARANCE' ? 'bg-blue-100 text-blue-700' :
                          cert.type === 'AS_BUILT' ? 'bg-teal-100 text-teal-700' :
                          cert.type === 'THIRD_PARTY' ? 'bg-purple-100 text-purple-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>{cert.type.replace(/_/g, ' ')}</span>
                        {cert.attached
                          ? <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-medium">✓ Attached</span>
                          : <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">⧖ Pending</span>
                        }
                      </div>
                      <div className="text-xs font-medium text-gray-900">{cert.title}</div>
                      <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-2 flex-wrap">
                        <span>{cert.ref}</span>
                        {cert.issuedBy && <span>· {cert.issuedBy}</span>}
                        {cert.issuedDate && <span>· {fmtDate(cert.issuedDate)}</span>}
                      </div>
                      {cert.result && <div className="text-xs text-gray-600 mt-1 italic">{cert.result}</div>}
                    </div>
                    {cert.attached
                      ? <button className="text-gray-400 hover:text-gray-600 p-1"><Eye className="w-3.5 h-3.5" /></button>
                      : <button className="text-xs bg-amber-100 text-amber-700 hover:bg-amber-200 px-2 py-1 rounded-lg font-medium flex items-center gap-1">
                          <UploadCloud className="w-3 h-3" />Upload
                        </button>
                    }
                  </div>
                </div>
              ))}
            </div>

            {/* Required certs from milestone config */}
            {milestone.requiredCerts.length > 0 && (
              <div>
                <div className="text-xs font-semibold text-gray-700 mb-1.5">Required by client for this milestone</div>
                <div className="space-y-1">
                  {milestone.requiredCerts.map((cert, i) => {
                    const linked = claim.qualityCerts.some(c => c.title.toLowerCase().includes(cert.toLowerCase().slice(0, 20)))
                    return (
                      <div key={i} className={`flex items-center gap-2 text-xs rounded-lg px-3 py-2 ${linked ? 'bg-green-50 text-green-800' : 'bg-gray-50 text-gray-600'}`}>
                        {linked ? <CheckCircle className="w-3 h-3 text-green-500 flex-shrink-0" /> : <Circle className="w-3 h-3 text-gray-300 flex-shrink-0" />}
                        {cert}
                      </div>
                    )
                  })}
                </div>
              </div>
            )}

            {(claim.componentQC === 'POPULATED' || claim.componentQC === 'PENDING') && missingCerts.length === 0 && (
              <button className="w-full py-2 text-xs font-semibold bg-green-100 text-green-700 hover:bg-green-200 rounded-lg flex items-center justify-center gap-1.5">
                <CheckCircle className="w-3.5 h-3.5" />Accept quality certifications
              </button>
            )}
          </div>
        )}
      </div>

      {/* Action footer */}
      <div className="border-t bg-white px-5 py-3">
        <div className="flex items-center gap-2 flex-wrap">

          {claim.status === 'DRAFT' && (
            <button className="btn-secondary flex items-center gap-2 text-xs">
              <ArrowRight className="w-3.5 h-3.5" />Move to review
            </button>
          )}

          {claim.status === 'IN_REVIEW' && canSign && !claim.declarationSignedBy && (
            <button className="btn-secondary flex items-center gap-2 text-xs" disabled={unpaidSuppliers.length > 0}>
              <ArrowRight className="w-3.5 h-3.5" />Mark as pending declaration
            </button>
          )}

          {(claim.status === 'IN_REVIEW' || claim.status === 'PENDING_DECLARATION') && canSign && claim.declarationSignedBy && (
            <button className="btn-primary flex items-center gap-2 text-xs">
              <CheckCircle className="w-3.5 h-3.5" />Mark ready to submit
            </button>
          )}

          {claim.status === 'READY_TO_SUBMIT' && canSubmit && (
            <>
              <button className="btn-primary flex items-center gap-2 text-sm">
                <Send className="w-4 h-4" />Submit to {job.clientType === 'TMR' ? 'TMR' : job.clientType === 'ENERGEX' ? 'Energex' : 'client'}
              </button>
              <button onClick={() => generateClaimBundlePDF(claim, job, milestone, false)} className="btn-secondary flex items-center gap-2 text-sm">
                <Download className="w-4 h-4" />Export claim bundle
              </button>
            </>
          )}

          {claim.status === 'SUBMITTED' && canRecordPayment && (
            <>
              <button className="btn-primary flex items-center gap-2 text-sm">
                <CheckCircle className="w-4 h-4" />Record approval
              </button>
              <button className="flex items-center gap-2 text-xs px-3 py-2 bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 rounded-lg font-medium" onClick={() => setExpandDispute(!expandDispute)}>
                <AlertTriangle className="w-3.5 h-3.5" />Mark disputed
              </button>
            </>
          )}

          {(claim.status === 'APPROVED' || claim.status === 'READY_TO_SUBMIT') && canRecordPayment && (
            <>
              <button className="btn-primary flex items-center gap-2 text-sm">
                <CheckCircle className="w-4 h-4" />Record payment received
              </button>
              {/* PM-11: Xero push — A08 Financial Agent, Wave 4 */}
              {!xeroSync
                ? <button
                    onClick={async () => {
                      setXeroSyncing(true)
                      try {
                        const today   = new Date().toISOString().slice(0, 10)
                        const dueDate = new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10)
                        const res = await fetch('https://trafficprocure-api.dirlayo69.workers.dev/api/accounting/push-invoice', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            type:        'ACCREC',
                            reference:   claim.claimRef,
                            contactName: job.clientName,
                            contactEmail: job.pmEmail,
                            date:        today,
                            dueDate,
                            lineItems: [
                              {
                                description: `Progress Payment Claim — ${milestone.title} (${claim.claimRef})`,
                                quantity:    1,
                                unitAmount:  fs.netClaimed,
                                accountCode: '200',
                                taxType:     'TAX003',  // AU GST on income 10%
                              },
                              ...(fs.retentionHeld > 0 ? [{
                                description: `Less retention withheld (${job.retentionPct}%)`,
                                quantity:    1,
                                unitAmount:  -fs.retentionHeld,
                                accountCode: '200',
                                taxType:     'TAX003',
                              }] : []),
                            ],
                          }),
                        })
                        const data = await res.json() as any
                        if (!res.ok) throw new Error(
                          (data.detail ? data.detail.slice(0, 300) : null) ?? data.error ?? 'Push failed'
                        )
                        const sync = recordXeroSync(claim.claimRef, claim.jobId, user?.fullName ?? 'PM')
                        setXeroSync({ ...sync, xeroInvoiceId: data.xeroInvoiceId ?? sync.xeroInvoiceId })
                      } catch (err: any) {
                        alert(`Xero push failed: ${err.message ?? 'Unknown error. Check Xero connection in Settings.'}`)
                      } finally {
                        setXeroSyncing(false)
                      }
                    }}
                    disabled={xeroSyncing}
                    className="flex items-center gap-1.5 text-xs px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium disabled:opacity-60">
                    {xeroSyncing
                      ? <><RefreshCw className="w-3.5 h-3.5 animate-spin" />Pushing to Xero…</>
                      : <><ExternalLink className="w-3.5 h-3.5" />Push to Xero</>}
                  </button>
                : <span className="flex items-center gap-1.5 text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 px-2.5 py-1.5 rounded-lg font-medium">
                    <CheckCircle className="w-3.5 h-3.5" />{xeroSync.xeroInvoiceId} · {new Date(xeroSync.syncedAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}
                  </span>
              }
            </>
          )}

          {claim.status === 'PAID' && (
            <div className="flex items-center gap-2 text-xs text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
              <CheckCircle className="w-3.5 h-3.5" />
              Paid {fmtDate(claim.paidDate)} — {fmtCurrency(claim.paidAmount ?? 0)}
            </div>
          )}

          {!['PAID','VOID'].includes(claim.status) && (
            <button onClick={() => generateClaimBundlePDF(claim, job, milestone, true)} className="ml-auto text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1">
              <Download className="w-3.5 h-3.5" />Export partial bundle
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Milestones tab ────────────────────────────────────────────────

function MilestonesTab({ scopedJobIds, canInitiateClaim }: {
  scopedJobIds: string[] | null
  canInitiateClaim: boolean
}) {
  const visibleJobs = (scopedJobIds
    ? CLAIM_JOBS.filter(j => scopedJobIds.includes(j.id))
    : CLAIM_JOBS
  ).filter(j => j.status === 'ACTIVE')

  const [activeJobId, setActiveJobId] = useState(visibleJobs[0]?.id ?? '')
  const [selected, setSelected] = useState<{ claim: ProgressClaim; milestone: Milestone; job: ClaimJob } | null>(null)
  const [liveMilestones, setLiveMilestones] = useState<Milestone[]>(() => getLiveMilestones())
  const [liveClaims] = useState<ProgressClaim[]>(() => getLiveClaims())
  const [eligibility, setEligibility] = useState<Record<string, MilestoneEligibilityResult>>({})
  const [liveEvidence, setLiveEvidence] = useState<Record<string, LiveEvidenceItem[]>>({})

  const job = CLAIM_JOBS.find(j => j.id === activeJobId)!
  const milestones = liveMilestones.filter(m => m.jobId === activeJobId)
  const fin = calcJobFinancials(activeJobId)

  // Compute eligibility for all milestones when job changes
  useEffect(() => {
    const cache: Record<string, MilestoneEligibilityResult> = {}
    const updatedMilestones = getLiveMilestones().map(ms => {
      if (['PAID', 'PAID_PARTIAL'].includes(ms.status)) return ms
      const result = computeMilestoneEligibility({
        linkedWIs: ms.linkedWIs, linkedITP: ms.linkedITP,
        linkedPOs: ms.linkedPOs, linkedDrawingTaskIds: ms.linkedDrawingTaskIds,
        wiIdMap: ms.wiIdMap,
      })
      cache[ms.id] = result
      const newStatus = result.eligible
        ? 'ELIGIBLE'
        : ms.status === 'ELIGIBLE' ? 'IN_PROGRESS' : ms.status
      if (newStatus !== ms.status) {
        updateMilestone(ms.id, { status: newStatus as any })
        return { ...ms, status: newStatus }
      }
      return ms
    })
    setEligibility(cache)
    setLiveMilestones(updatedMilestones as any)

    // Pull live evidence for milestones with linked WIs
    const evidenceMap: Record<string, LiveEvidenceItem[]> = {}
    updatedMilestones.filter(ms => ms.jobId === activeJobId).forEach(ms => {
      if (ms.linkedWIs.length > 0) {
        evidenceMap[ms.id] = pullLiveEvidence(ms.linkedWIs, ms.wiIdMap ?? {})
      }
    })
    setLiveEvidence(evidenceMap)
  }, [activeJobId])

  const totalRetentionHeld = milestones
    .filter(m => ['PAID', 'CLAIM_IN_PROGRESS', 'SUBMITTED'].includes(m.status))
    .reduce((s, m) => s + m.retentionHeld, 0)

  return (
    <div className="space-y-4">
      {/* Job selector */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1">
        <span className="text-xs text-gray-500 flex-shrink-0">Job:</span>
        {visibleJobs.map(j => {
          const eligible = liveMilestones.filter(m => m.jobId === j.id && m.status === 'ELIGIBLE').length
          return (
            <button key={j.id} onClick={() => { setActiveJobId(j.id); setSelected(null) }}
              className={`flex-shrink-0 flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${activeJobId === j.id ? 'bg-[#1F3864] text-white border-[#1F3864]' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'}`}>
              {j.jobNumber}
              {eligible > 0 && (
                <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${activeJobId === j.id ? 'bg-green-400 text-white' : 'bg-green-100 text-green-700'}`}>{eligible} eligible</span>
              )}
            </button>
          )
        })}
      </div>

      {/* Job financial summary */}
      {job && (
        <div className="bg-[#1F3864] rounded-xl p-4 text-white">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <div className="text-xs text-blue-300 mb-0.5">{job.clientType} · {job.contractRef}</div>
              <div className="font-semibold text-sm leading-snug">{job.projectName}</div>
              <div className="text-xs text-blue-300 mt-1">PM: {job.pmName} · Claims due day {job.claimPeriodDay} of each month</div>
            </div>
            <div className="text-right flex-shrink-0">
              <ProgressRing pct={fin.pctComplete} size={52} />
              <div className="text-[10px] text-blue-300 mt-1">{fin.pctComplete}% claimed</div>
            </div>
          </div>
          <div className="grid grid-cols-4 gap-2">
            {[
              { label: 'Contract value', value: fmtCurrency(job.contractValue), sub: '' },
              { label: 'Total claimed', value: fmtCurrency(fin.totalClaimed), sub: 'net ex retention' },
              { label: 'Total paid', value: fmtCurrency(fin.totalPaid), sub: 'received' },
              { label: 'Retention held', value: fmtCurrency(totalRetentionHeld), sub: `${job.retentionPct}% per milestone` },
            ].map(({ label, value, sub }) => (
              <div key={label} className="bg-white/10 rounded-lg p-2.5">
                <div className="text-xs font-bold">{value}</div>
                <div className="text-[10px] text-blue-300">{label}</div>
                {sub && <div className="text-[10px] text-blue-400/70">{sub}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Milestone timeline + optional detail panel side-by-side */}
      <div className={`flex gap-4 items-start ${selected ? '' : ''}`}>
        <div className="flex-1 min-w-0">
          <div className="space-y-2">
            {milestones.map((ms, idx) => {
              const claim = liveClaims.find(c => c.milestoneId === ms.id && !['VOID'].includes(c.status))
              const elig = eligibility[ms.id]
              const evidence = liveEvidence[ms.id] ?? []
              const isSelected = selected?.milestone.id === ms.id
              const pctOfContract = Math.round(ms.claimAmount / job.contractValue * 100)
              const isUrgent = ms.status === 'ELIGIBLE' && ms.submissionDeadline &&
                new Date(ms.submissionDeadline).getTime() - Date.now() < 7 * 24 * 60 * 60 * 1000

              return (
                <div key={ms.id}
                  className={`bg-white border rounded-xl p-4 transition-all ${isSelected ? 'border-[#2E75B6] ring-2 ring-blue-100 shadow-sm' : isUrgent ? 'border-amber-300' : 'border-gray-200 hover:border-gray-300'}`}>
                  <div className="flex items-start gap-3">
                    {/* Sequence indicator */}
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5 ${
                      ms.status === 'PAID' ? 'bg-teal-500 text-white' :
                      ms.status === 'ELIGIBLE' ? 'bg-green-500 text-white' :
                      ms.status === 'CLAIM_IN_PROGRESS' ? 'bg-blue-500 text-white' :
                      ms.status === 'SUBMITTED' ? 'bg-purple-500 text-white' :
                      'bg-gray-200 text-gray-500'
                    }`}>{idx + 1}</div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <div className="text-xs font-mono text-gray-400 mb-0.5">{ms.reference}</div>
                          <div className="text-sm font-medium text-gray-900 leading-snug">{ms.title}</div>
                        </div>
                        <MsStatusBadge status={ms.status} />
                      </div>

                      <div className="flex items-center gap-3 mt-2 flex-wrap">
                        <span className="text-xs font-bold text-gray-900">{fmtCurrency(ms.claimAmount)}</span>
                        <span className="text-xs text-gray-400">{pctOfContract}% of contract</span>
                        {ms.retentionHeld > 0 && (
                          <span className="text-xs text-amber-600">− {fmtCurrency(ms.retentionHeld)} retention</span>
                        )}
                        {ms.submissionDeadline && ms.status !== 'PAID' && (
                          <span className={`text-xs flex items-center gap-1 ${isUrgent ? 'text-amber-600 font-semibold' : 'text-gray-400'}`}>
                            <Clock className="w-3 h-3" />Due {fmtDate(ms.submissionDeadline)}
                          </span>
                        )}
                        {ms.status === 'PAID' && ms.paidDate && (
                          <span className="text-xs text-teal-600 font-medium flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" />Paid {fmtDate(ms.paidDate)} — {fmtCurrency(ms.paidAmount ?? 0)}
                          </span>
                        )}
                      </div>

                      {/* Live eligibility — Phase 2 */}
                      {elig && !elig.eligible && ms.status !== 'PAID' && (
                        <div className="mt-2 space-y-1">
                          {elig.blockingReasons.slice(0, 3).map((b, i) => (
                            <div key={i} className="flex items-start gap-1.5 text-[11px] text-red-700 bg-red-50 border border-red-200 rounded-lg px-2 py-1">
                              <span className="flex-shrink-0 mt-0.5">⛔</span>
                              <span><strong>{b.type.replace('_',' ')}:</strong> {b.description}</span>
                            </div>
                          ))}
                          {elig.blockingReasons.length > 3 && (
                            <div className="text-[11px] text-red-600 px-2">+{elig.blockingReasons.length - 3} more blocking issues</div>
                          )}
                        </div>
                      )}
                      {elig?.eligible && ms.status !== 'PAID' && (
                        <div className="mt-2 flex items-center gap-1.5 text-[11px] text-green-700 bg-green-50 border border-green-200 rounded-lg px-2 py-1">
                          <CheckCircle className="w-3 h-3 flex-shrink-0" />
                          <span>All conditions verified — ready to claim ({elig.wiStatuses.length} WI{elig.wiStatuses.length!==1?'s':''} complete, {evidence.length} evidence items)</span>
                        </div>
                      )}
                      {evidence.length > 0 && ms.status !== 'PAID' && elig && !elig.eligible === false && (
                        <div className="mt-1 text-[11px] text-purple-600 flex items-center gap-1">
                          <Camera className="w-3 h-3" />{evidence.length} field evidence item{evidence.length!==1?'s':''} available for this claim
                        </div>
                      )}

                      {/* Claim progress if in-progress */}
                      {claim && !['PAID', 'VOID'].includes(claim.status) && (
                        <div className="mt-2 flex items-center gap-2">
                          <div className="flex gap-1">
                            {[claim.componentFS, claim.componentEW, claim.componentSD, claim.componentFD, claim.componentQC].map((s, i) => (
                              <div key={i} className={`w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold ${
                                s === 'ACCEPTED' ? 'bg-green-500 text-white' :
                                s === 'FLAGGED' ? 'bg-red-500 text-white' :
                                s === 'POPULATED' ? 'bg-amber-400 text-white' :
                                'bg-gray-200 text-gray-400'
                              }`}>{['F','E','S','D','Q'][i]}</div>
                            ))}
                          </div>
                          <ClaimStatusBadge status={claim.status} />
                          <button
                            onClick={() => setSelected(selected?.claim.id === claim.id ? null : { claim, milestone: ms, job })}
                            className="text-xs text-[#2E75B6] hover:underline ml-auto"
                          >
                            {isSelected ? 'Close' : 'Open claim →'}
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Primary CTA */}
                    {ms.status === 'ELIGIBLE' && canInitiateClaim && !claim && (
                      <button className="flex-shrink-0 btn-primary text-xs flex items-center gap-1.5 py-1.5 px-3">
                        <Plus className="w-3.5 h-3.5" />Create claim
                      </button>
                    )}
                    {ms.status === 'PAID' && (
                      <CheckCircle className="w-5 h-5 text-teal-500 flex-shrink-0 mt-1" />
                    )}
                    {(ms.status === 'CLAIM_IN_PROGRESS' || ms.status === 'SUBMITTED') && claim && (
                      <button
                        onClick={() => setSelected(selected?.claim.id === claim.id ? null : { claim, milestone: ms, job })}
                        className={`flex-shrink-0 p-1.5 rounded-lg transition-colors ${isSelected ? 'bg-blue-100 text-blue-600' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`}
                      >
                        <ChevronRight className={`w-4 h-4 transition-transform ${isSelected ? 'rotate-90' : ''}`} />
                      </button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Detail panel */}
        {selected && (
          <div className="w-[480px] flex-shrink-0 bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm sticky top-6"
            style={{ maxHeight: 'calc(100vh - 160px)' }}>
            <ClaimDetailPanel
              claim={selected.claim}
              job={selected.job}
              milestone={selected.milestone}
              onClose={() => setSelected(null)}
              canSign={true}
              canSubmit={true}
              canRecordPayment={true}
            />
          </div>
        )}
      </div>
    </div>
  )
}

// ── Active Claims tab ─────────────────────────────────────────────

function ActiveClaimsTab({ scopedJobIds, canSign, canSubmit, canRecordPayment }: {
  scopedJobIds: string[] | null
  canSign: boolean
  canSubmit: boolean
  canRecordPayment: boolean
}) {
  const [statusFilter, setStatusFilter] = useState<ClaimStatus | 'ALL'>('ALL')
  const [selected, setSelected] = useState<{ claim: ProgressClaim; milestone: Milestone; job: ClaimJob } | null>(null)
  const [claimsData] = useState<ProgressClaim[]>(() => getLiveClaims())

  const allClaims = claimsData.filter(c => {
    if (scopedJobIds && !scopedJobIds.includes(c.jobId)) return false
    if (statusFilter !== 'ALL' && c.status !== statusFilter) return false
    return true
  })

  const actionNeeded = claimsData.filter(c => {
    if (scopedJobIds && !scopedJobIds.includes(c.jobId)) return false
    return ['IN_REVIEW', 'PENDING_DECLARATION', 'APPROVED', 'DISPUTED'].includes(c.status) ||
           hasFlaggedComponent(c)
  }).length

  const urgentStatuses: ClaimStatus[] = ['IN_REVIEW', 'PENDING_DECLARATION', 'READY_TO_SUBMIT', 'SUBMITTED', 'APPROVED', 'DISPUTED']

  return (
    <div className="space-y-4">
      {/* Alert banner */}
      {actionNeeded > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-center gap-3">
          <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
          <p className="text-sm text-amber-800">
            <strong>{actionNeeded} claim{actionNeeded !== 1 ? 's' : ''}</strong> require{actionNeeded === 1 ? 's' : ''} action — review flagged components or pending sign-offs.
          </p>
        </div>
      )}

      {/* Status filter */}
      <div className="flex gap-2 flex-wrap items-center">
        {(['ALL', ...urgentStatuses, 'PAID', 'VOID'] as (ClaimStatus | 'ALL')[]).map(f => {
          const count = f === 'ALL' ? claimsData.filter(c => !scopedJobIds || scopedJobIds.includes(c.jobId)).length
            : claimsData.filter(c => c.status === f && (!scopedJobIds || scopedJobIds.includes(c.jobId))).length
          if (f !== 'ALL' && count === 0) return null
          return (
            <button key={f} onClick={() => setStatusFilter(f)}
              className={`text-xs px-3 py-1.5 rounded-full font-medium flex items-center gap-1.5 transition-colors ${statusFilter === f ? 'bg-[#1F3864] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              {f === 'ALL' ? 'All claims' : CLAIM_STATUS_META[f].label}
              <span className={`text-[10px] font-bold rounded-full px-1.5 ${statusFilter === f ? 'bg-white/20' : 'bg-white text-gray-500'}`}>{count}</span>
            </button>
          )
        })}
      </div>

      <div className={`flex gap-4 items-start`}>
        <div className="flex-1 min-w-0 space-y-2">
          {allClaims.length === 0 ? (
            <div className="bg-white border border-dashed border-gray-300 rounded-xl flex flex-col items-center py-16 text-center">
              <FileText className="w-10 h-10 text-gray-300 mb-3" />
              <div className="font-medium text-gray-500">No claims in this status</div>
            </div>
          ) : allClaims.map(claim => {
            const milestone = MOCK_MILESTONES.find(m => m.id === claim.milestoneId)!
            const job = CLAIM_JOBS.find(j => j.id === claim.jobId)!
            const accepted = countComponentsAccepted(claim)
            const flagged = hasFlaggedComponent(claim)
            const isSelected = selected?.claim.id === claim.id
            const isUrgent = ['PENDING_DECLARATION', 'READY_TO_SUBMIT', 'DISPUTED'].includes(claim.status) || flagged

            return (
              <div key={claim.id} onClick={() => setSelected(isSelected ? null : { claim, milestone, job })}
                className={`bg-white border rounded-xl p-4 cursor-pointer transition-all hover:shadow-sm ${
                  isSelected ? 'border-blue-400 ring-2 ring-blue-100 shadow-sm' :
                  isUrgent ? 'border-amber-300' : 'border-gray-200 hover:border-gray-300'
                }`}>
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="text-xs font-mono text-gray-400">{claim.claimRef}</span>
                      <span className="text-xs text-gray-400">·</span>
                      <span className="text-xs text-gray-500">{job.jobNumber}</span>
                      <ClaimStatusBadge status={claim.status} />
                      {flagged && <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full font-medium">⚑ Action needed</span>}
                    </div>
                    <div className="text-sm font-medium text-gray-900 leading-snug mb-1.5">{milestone.title}</div>
                    <div className="flex items-center gap-3 text-xs text-gray-500 flex-wrap">
                      <span className="flex items-center gap-1"><Building className="w-3 h-3" />{job.clientName.split(' ').slice(0, 3).join(' ')}</span>
                      <span className="font-bold text-gray-700">{fmtCurrency(claim.financialSummary.netClaimed)}</span>
                      <span>Updated {fmtDate(claim.updatedAt)}</span>
                    </div>
                    {/* Component pips */}
                    <div className="flex items-center gap-2 mt-2">
                      <div className="flex gap-1">
                        {[
                          { key: 'F', status: claim.componentFS },
                          { key: 'E', status: claim.componentEW },
                          { key: 'S', status: claim.componentSD },
                          { key: 'D', status: claim.componentFD },
                          { key: 'Q', status: claim.componentQC },
                        ].map(({ key, status }) => (
                          <div key={key} title={COMPONENT_STATUS_META[status as keyof typeof COMPONENT_STATUS_META]?.label}
                            className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold ${
                              status === 'ACCEPTED' ? 'bg-green-500 text-white' :
                              status === 'FLAGGED' ? 'bg-red-500 text-white' :
                              status === 'POPULATED' ? 'bg-amber-400 text-white' :
                              'bg-gray-200 text-gray-400'
                            }`}>{key}</div>
                        ))}
                      </div>
                      <span className="text-xs text-gray-400">{accepted}/5 components accepted</span>
                    </div>
                  </div>
                  <ChevronRight className={`w-4 h-4 text-gray-400 flex-shrink-0 mt-1 ${isSelected ? 'text-blue-500' : ''}`} />
                </div>
              </div>
            )
          })}
        </div>

        {/* Detail panel */}
        {selected && (
          <div className="w-[480px] flex-shrink-0 bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm sticky top-6"
            style={{ maxHeight: 'calc(100vh - 160px)' }}>
            <ClaimDetailPanel
              claim={selected.claim}
              job={selected.job}
              milestone={selected.milestone}
              onClose={() => setSelected(null)}
              canSign={canSign}
              canSubmit={canSubmit}
              canRecordPayment={canRecordPayment}
            />
          </div>
        )}
      </div>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────




// ─── GST formatting helpers ────────────────────────────────────────
// fmtX: plain $ amount (no AUD prefix — assumed AUD for all documents)
// gstRow: returns three table rows for ex-GST / GST / total presentation
function fmtX(n: number): string {
  return '$' + n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}
function gstRows(exGST: number, label = 'Amount'): string {
  const gst   = exGST * 0.1
  const total = exGST + gst
  return `
    <tr style="border-top:1px solid #e5e7eb">
      <td colspan="999" style="padding:2px"></td>
    </tr>
    <tr>
      <td colspan="2" style="padding:5px 10px;text-align:right;font-size:9.5pt;color:#555">${label} (ex GST)</td>
      <td style="padding:5px 10px;text-align:right;font-family:monospace">${fmtX(exGST)}</td>
    </tr>
    <tr>
      <td colspan="2" style="padding:5px 10px;text-align:right;font-size:9.5pt;color:#555">GST (10%)</td>
      <td style="padding:5px 10px;text-align:right;font-family:monospace">${fmtX(gst)}</td>
    </tr>
    <tr style="background:#f0f9ff">
      <td colspan="2" style="padding:6px 10px;text-align:right;font-weight:bold">Total (inc GST)</td>
      <td style="padding:6px 10px;text-align:right;font-weight:bold;font-family:monospace">${fmtX(total)}</td>
    </tr>`
}

// ─────────────────────────────────────────────────────────────────
// PHASE 7 — STATUTORY DECLARATION PDF (QLD)
// Pre-filled statutory declaration for signature — required before
// a claim can be submitted to TMR, Energex, or Council.
// ─────────────────────────────────────────────────────────────────
function generateStatutoryDeclarationPDF(claim: ProgressClaim, job: ClaimJob, milestone: Milestone) {
  const now = new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })
  const fmtAUD = fmtX

  const supplierRows = claim.supplierPayments.map((s, i) => `
    <tr style="background:${i%2===0?'#fff':'#f9fafb'}">
      <td style="padding:6px 10px">${s.supplierName}</td>
      <td style="padding:6px 10px;font-family:monospace;font-size:9pt">${s.abn}</td>
      <td style="padding:6px 10px;font-family:monospace;font-size:9pt">${s.poRef}</td>
      <td style="padding:6px 8px;text-align:right">${fmtAUD(s.amount)}</td>
      <td style="padding:6px 8px;text-align:center;font-weight:bold;color:${s.status==='PAID'?'#166534':'#b91c1c'}">${s.status}</td>
    </tr>`).join('')

  const html = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8">
<title>Statutory Declaration — ${claim.claimRef}</title>
<style>
*{box-sizing:border-box}body{font-family:Arial,sans-serif;font-size:11pt;color:#111;margin:22mm}
h1{font-size:18pt;font-weight:900;color:#1B3A6B;margin:0 0 4px;text-align:center;text-transform:uppercase;letter-spacing:1px}
.sub{text-align:center;font-size:10pt;color:#666;margin-bottom:20px}
.hdr{border-bottom:3px solid #1B3A6B;padding-bottom:10px;margin-bottom:18px}
.info table{width:100%;border-collapse:collapse;margin-bottom:14px}
.info td{padding:5px 8px;border-bottom:1px solid #f3f4f6}.info td:first-child{width:36%;font-weight:bold;color:#555;background:#f9fafb}
.dec-text{line-height:2;font-size:11pt;margin:18px 0;padding:16px 20px;border:1px solid #ccc;background:#fafafa;border-radius:4px}
table.sup{width:100%;border-collapse:collapse;font-size:9.5pt;margin-bottom:14px}
th{background:#1B3A6B;color:#fff;padding:6px 10px;text-align:left}
.sig-block{margin-top:24px}
.sig-line{border-bottom:1.5px solid #333;height:34px;margin-bottom:4px}
.sig-lbl{font-size:9pt;color:#888}
.witness-box{border:2px solid #1B3A6B;border-radius:5px;padding:14px 18px;margin-top:20px}
@media print{body{margin:16mm}}
</style></head><body>
${buildDocumentHeader('INVOICE')}
<div class="hdr">
  <h1>Statutory Declaration</h1>
  <div class="sub">Oaths Act 1867 (Qld)</div>
</div>

<div class="info"><table>
  <tr><td>Claim reference</td><td><strong>${claim.claimRef}</strong></td></tr>
  <tr><td>Milestone</td><td>${milestone.title}</td></tr>
  <tr><td>Project</td><td>${job.projectName}</td></tr>
  <tr><td>Contract reference</td><td>${job.contractRef}</td></tr>
  <tr><td>Client</td><td>${job.clientName}</td></tr>
  <tr><td>Date of declaration</td><td>${now}</td></tr>
</table></div>

<div class="dec-text">
  I, <strong>${job.pmName}</strong>, of MCB Civil &amp; Electrical Pty Ltd (ABN 44 128 993 087), do solemnly and sincerely declare that all subcontractors and suppliers listed in the payment schedule below have been paid in full for all work, services, and materials supplied in connection with the works described in Claim ${claim.claimRef} — ${milestone.title}, and I make this solemn declaration conscientiously believing the same to be true and by virtue of the provisions of the <em>Oaths Act 1867</em> (QLD).
</div>

<p style="font-weight:bold;font-size:10.5pt;margin-bottom:6px">Supplier and subcontractor payment schedule</p>
<table class="sup">
  <thead><tr><th>Supplier / subcontractor</th><th>ABN</th><th>PO reference</th><th style="text-align:right">Amount (ex GST)</th><th style="text-align:center">Status</th></tr></thead>
  <tbody>${supplierRows}</tbody>
</table>

<div class="sig-block">
  <div class="sig-line"></div>
  <div class="sig-lbl">Declarant signature — ${job.pmName}, Project Manager, MCB Civil Electrical Pty Ltd</div>
</div>

<div class="witness-box">
  <p style="font-size:10pt;font-weight:bold;color:#1B3A6B;margin-bottom:10px">Declared before me at: _________________________ on this _____ day of _____________ 20____</p>
  <div class="sig-line"></div>
  <div class="sig-lbl">Justice of the Peace / Commissioner for Declarations — name, registration number &amp; signature</div>
</div>

<div class="footer">Oaths Act 1867 (Qld) — MCB Civil Electrical Pty Ltd</div>
</body></html>`
  const w = window.open('', '_blank', 'width=960,height=800')
  if (!w) { alert('Pop-up blocked'); return }
  w.document.write(html); w.document.close()
  setTimeout(() => { w.focus(); w.print() }, 350)
}


// ─────────────────────────────────────────────────────────────────
// PHASE 7 — CLAIM FORM GENERATOR
// Generates the correct statutory payment claim format based on
// client type:
//   TMR      → TMR Milestone Claim Form (TP-MCF)
//   Other    → SOPA QLD Payment Claim (Building Industry Fairness
//              (Security of Payment) Act 2017 (Qld) compliant)
// ─────────────────────────────────────────────────────────────────
function generateClaimForm(claim: ProgressClaim, job: ClaimJob, milestone: Milestone) {
  const now    = new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })
  const fmtAUD = fmtX
  const fs = claim.financialSummary

  if (job.clientType === 'TMR') {
    // ── TMR MILESTONE CLAIM FORM (TP-MCF) ──────────────────────
    const html = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8">
<title>TMR Milestone Claim Form — ${claim.claimRef}</title>
<style>
*{box-sizing:border-box}body{font-family:Arial,sans-serif;font-size:10.5pt;color:#111;margin:18mm}
.tmr-hdr{background:#003a70;color:#fff;padding:14px 20px;border-radius:6px 6px 0 0;display:flex;justify-content:space-between;align-items:center}
.tmr-hdr h1{font-size:16pt;font-weight:900;margin:0}
.form-code{font-family:monospace;font-size:12pt;font-weight:bold;background:rgba(255,255,255,.15);padding:4px 12px;border-radius:4px}
.body-box{border:2px solid #003a70;border-top:none;border-radius:0 0 6px 6px;padding:18px 20px}
h2{font-size:10.5pt;color:#003a70;border-bottom:1.5px solid #003a70;padding-bottom:3px;margin:14px 0 8px}
.info table{width:100%;border-collapse:collapse;margin-bottom:10px}
.info td{padding:4px 8px;border-bottom:1px solid #f3f4f6}.info td:first-child{width:38%;font-weight:bold;color:#555;background:#f9fafb}
.amt-box{border:2px solid #003a70;border-radius:5px;padding:12px 16px;margin:12px 0;display:flex;gap:24px}
.amt{flex:1;text-align:center}.amt-l{font-size:9pt;color:#666}.amt-v{font-size:15pt;font-weight:900;color:#003a70}
.cert-box{background:#eff6ff;border:1px solid #93c5fd;border-radius:4px;padding:10px 14px;margin:14px 0;font-size:9.5pt}
.sig-row{display:flex;gap:30px;margin-top:18px}
.sig-block{flex:1}.sig-line{border-bottom:1.5px solid #333;height:32px;margin:14px 0 4px}.sig-lbl{font-size:8pt;color:#888}
.footer{font-size:7.5pt;color:#aaa;text-align:center;margin-top:18px;border-top:1px solid #eee;padding-top:7px}
@media print{body{margin:14mm}}
</style></head><body>

<div class="tmr-hdr">
  <h1>Department of Transport and Main Roads<br><span style="font-size:11pt;font-weight:400">Milestone Claim Form</span></h1>
  <div class="form-code">TP-MCF</div>
</div>
<div class="body-box">

<div class="info"><table>
  <tr><td>Contract reference</td><td><strong>${job.contractRef}</strong></td></tr>
  <tr><td>Project name</td><td>${job.projectName}</td></tr>
  <tr><td>Contractor</td><td>MCB Civil Electrical Pty Ltd · ABN 44 128 993 087</td></tr>
  <tr><td>Claim reference</td><td><strong>${claim.claimRef}</strong></td></tr>
  <tr><td>Milestone reference</td><td>${milestone.reference}</td></tr>
  <tr><td>Milestone description</td><td>${milestone.title}</td></tr>
  <tr><td>Invoice number</td><td>${fs.invoiceNumber}</td></tr>
  <tr><td>Date of claim</td><td>${now}</td></tr>
  <tr><td>TMR contact</td><td>${milestone.clientContactName ?? '—'} · ${milestone.clientContactEmail ?? '—'}</td></tr>
</table></div>

<h2>Claim amount</h2>
<div class="amt-box">
  <div class="amt"><div class="amt-l">Milestone value</div><div class="amt-v">${fmtAUD(fs.milestoneAmount)}</div></div>
  <div class="amt"><div class="amt-l">Retention held</div><div class="amt-v">${fmtAUD(fs.retentionHeld)}</div></div>
  <div class="amt"><div class="amt-l">Net claimed (ex GST)</div><div class="amt-v">${fmtAUD(fs.netClaimed)}</div></div>
  <div class="amt"><div class="amt-l">GST (10%)</div><div class="amt-v">${fmtAUD(fs.gstAmount)}</div></div>
  <div class="amt" style="background:#dbeafe;border-radius:6px;padding:10px"><div class="amt-l" style="font-weight:bold">Total (inc GST)</div><div class="amt-v" style="color:#1B3A6B">${fmtAUD(fs.totalIncGST)}</div></div>
</div>

<h2>Cumulative claim history</h2>
<div class="info"><table>
  <tr><td>Previously certified</td><td>${fmtAUD(fs.previousPayments)}</td></tr>
  <tr><td>This claim (ex GST)</td><td>${fmtAUD(fs.netClaimed)}</td></tr>
  <tr><td>Cumulative claimed to date</td><td><strong>${fmtAUD(fs.previousPayments + fs.netClaimed)}</strong></td></tr>
  <tr><td>Balance of contract remaining</td><td>${fmtAUD(fs.balanceRemaining)}</td></tr>
</table></div>

<div class="cert-box">
  📋 This milestone claim is supported by the attached evidence of works (photographic records with GPS/timestamp), quality certifications, and statutory declaration as required under TMR contract conditions. Linked work instructions: ${milestone.linkedWIs.join(', ') || '—'}. Linked hold points cleared: ${milestone.linkedITP.join(', ') || '—'}.
</div>

<h2>Certification</h2>
<p style="font-size:9.5pt;color:#555">I certify that the works described in this milestone claim have been completed in accordance with the contract specification and that the amount claimed is properly due and payable.</p>
<div class="sig-row">
  <div class="sig-block"><div class="sig-line"></div><div class="sig-lbl">${job.pmName} — Project Manager, MCB Civil Electrical · date</div></div>
  <div class="sig-block"><div class="sig-line"></div><div class="sig-lbl">TMR Superintendent's Representative — certified amount &amp; date</div></div>
</div>
</div>
<div class="footer">Department of Transport and Main Roads · Form TP-MCF · ${claim.claimRef} · ${job.contractRef} · Generated ${now}</div>
</body></html>`
    const w = window.open('', '_blank', 'width=1000,height=820')
    if (!w) { alert('Pop-up blocked'); return }
    w.document.write(html); w.document.close()
    setTimeout(() => { w.focus(); w.print() }, 350)
    return
  }

  // ── SOPA QLD PAYMENT CLAIM (BIF Act 2017 compliant) ───────────
  const clientLabel = job.clientType === 'ENERGEX' ? 'Energex Limited' : job.clientType === 'COUNCIL' ? job.clientName : job.clientName
  const html = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8">
<title>Payment Claim (SOPA QLD) — ${claim.claimRef}</title>
<style>
*{box-sizing:border-box}body{font-family:Arial,sans-serif;font-size:10.5pt;color:#111;margin:18mm}
h1{font-size:18pt;font-weight:900;color:#1B3A6B;margin:0}
.hdr{border-bottom:3px solid #1B3A6B;padding-bottom:10px;margin-bottom:16px;display:flex;justify-content:space-between;align-items:flex-end}
.bif-banner{background:#1B3A6B;color:#fff;text-align:center;padding:8px 16px;border-radius:4px;font-size:10.5pt;font-weight:bold;margin-bottom:14px}
h2{font-size:10.5pt;color:#1B3A6B;border-bottom:1.5px solid #1B3A6B;padding-bottom:3px;margin:14px 0 8px}
.info table{width:100%;border-collapse:collapse;margin-bottom:10px}
.info td{padding:4px 8px;border-bottom:1px solid #f3f4f6}.info td:first-child{width:38%;font-weight:bold;color:#555;background:#f9fafb}
.amt-box{border:2px solid #1B3A6B;border-radius:5px;padding:12px 16px;margin:12px 0;display:flex;gap:24px}
.amt{flex:1;text-align:center}.amt-l{font-size:9pt;color:#666}.amt-v{font-size:15pt;font-weight:900;color:#1B3A6B}
.notice-box{background:#fef3c7;border:1.5px solid #fbbf24;border-radius:4px;padding:10px 14px;margin:14px 0;font-size:9.5pt;color:#92400e}
.sig-row{display:flex;gap:30px;margin-top:18px}
.sig-block{flex:1}.sig-line{border-bottom:1.5px solid #333;height:32px;margin:14px 0 4px}.sig-lbl{font-size:8pt;color:#888}
.footer{font-size:7.5pt;color:#aaa;text-align:center;margin-top:18px;border-top:1px solid #eee;padding-top:7px}
@media print{body{margin:14mm}}
</style></head><body>
${buildDocumentHeader('INVOICE')}
<div style="text-align:right;margin-top:-10px;margin-bottom:16px">
  <div style="font-size:9pt;color:#888">Date: ${now}</div><div style="font-size:9pt;color:#888">Ref: ${claim.claimRef}</div>
</div>

<div class="bif-banner">PAYMENT CLAIM — Building Industry Fairness (Security of Payment) Act 2017 (Qld)</div>

<div class="info"><table>
  <tr><td>To (respondent)</td><td><strong>${clientLabel}</strong></td></tr>
  <tr><td>Contract reference</td><td>${job.contractRef}</td></tr>
  <tr><td>Project</td><td>${job.projectName}</td></tr>
  <tr><td>Claim reference</td><td><strong>${claim.claimRef}</strong></td></tr>
  <tr><td>Milestone / reference period</td><td>${milestone.title}</td></tr>
  <tr><td>Invoice number</td><td>${fs.invoiceNumber}</td></tr>
  <tr><td>Date of this payment claim</td><td>${now}</td></tr>
</table></div>

<h2>Claimed amount</h2>
<div class="amt-box">
  <div class="amt"><div class="amt-l">Work value (ex GST)</div><div class="amt-v">${fmtAUD(fs.milestoneAmount)}</div></div>
  <div class="amt"><div class="amt-l">Retention held</div><div class="amt-v">${fmtAUD(fs.retentionHeld)}</div></div>
  <div class="amt"><div class="amt-l">GST</div><div class="amt-v">${fmtAUD(fs.gstAmount)}</div></div>
  <div class="amt"><div class="amt-l">Total claimed (inc GST)</div><div class="amt-v">${fmtAUD(fs.totalIncGST)}</div></div>
</div>

<h2>Identification of construction work / goods and services</h2>
<p style="font-size:10pt">${milestone.title}</p>

<div class="notice-box">
  <strong>⚠ Notice under s.75 Building Industry Fairness (Security of Payment) Act 2017 (Qld):</strong> This is a payment claim made under the Building Industry Fairness (Security of Payment) Act 2017 (Qld). If you fail to provide a payment schedule responding to this payment claim within the period required by the Act, you become liable to pay the full amount of this claim. A payment schedule must be provided within 15 business days after this payment claim is given, or such shorter period as may be provided for under the contract.
</div>

<h2>Cumulative claim history</h2>
<div class="info"><table>
  <tr><td>Previously certified/paid</td><td>${fmtAUD(fs.previousPayments)}</td></tr>
  <tr><td>This claim (ex GST)</td><td>${fmtAUD(fs.netClaimed)}</td></tr>
  <tr><td>Cumulative claimed to date</td><td><strong>${fmtAUD(fs.previousPayments + fs.netClaimed)}</strong></td></tr>
  <tr><td>Balance of contract remaining</td><td>${fmtAUD(fs.balanceRemaining)}</td></tr>
</table></div>

<p style="font-size:9.5pt;color:#555">This payment claim is supported by evidence of works completed, quality certifications, and a statutory declaration confirming all subcontractors and suppliers have been paid for prior claimed works, all attached as part of the full claim bundle.</p>

<div class="sig-row">
  <div class="sig-block"><div class="sig-line"></div><div class="sig-lbl">${job.pmName} — Project Manager, MCB Civil Electrical Pty Ltd · date</div></div>
</div>

<div class="footer">Building Industry Fairness (Security of Payment) Act 2017 (Qld) — MCB Civil Electrical Pty Ltd</div>
</body></html>`
  const w = window.open('', '_blank', 'width=1000,height=820')
  if (!w) { alert('Pop-up blocked'); return }
  w.document.write(html); w.document.close()
  setTimeout(() => { w.focus(); w.print() }, 350)
}


// ─────────────────────────────────────────────────────────────────
// PHASE 7 — FULL CLAIM BUNDLE PDF
// Combines all 5 claim components (FS, EW, SD, FD, QC) into a
// single comprehensive submission document.
// ─────────────────────────────────────────────────────────────────
function generateClaimBundlePDF(claim: ProgressClaim, job: ClaimJob, milestone: Milestone, isPartial: boolean = false) {
  const now    = new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })
  const fmtAUD = fmtX
  const fmtDT  = (iso?: string) => iso ? new Date(iso).toLocaleString('en-AU', { dateStyle:'medium', timeStyle:'short' }) : '—'
  const fs = claim.financialSummary

  const poRows = fs.poLineItems.map((p, i) => `
    <tr style="background:${i%2===0?'#fff':'#f9fafb'}">
      <td style="padding:5px 10px">${p.description}</td>
      <td style="padding:5px 10px;font-size:9pt">${p.supplier}</td>
      <td style="padding:5px 8px;text-align:right;font-weight:bold">${fmtAUD(p.amount)}</td>
      <td style="padding:5px 8px;text-align:center;font-size:9pt">${p.status}</td>
    </tr>`).join('')

  const photoRows = claim.evidencePhotos.filter(p => p.included).map((p, i) => `
    <tr style="background:${i%2===0?'#fff':'#f9fafb'}">
      <td style="padding:5px 10px;font-family:monospace;font-size:9pt">${p.wiRef}</td>
      <td style="padding:5px 10px;font-size:9.5pt">${p.stepTitle}</td>
      <td style="padding:5px 8px;font-size:9pt">${p.crew}</td>
      <td style="padding:5px 8px;text-align:center;font-size:8.5pt">${fmtDT(p.timestamp)}</td>
      <td style="padding:5px 8px;font-size:8.5pt">${p.gpsCoordsLat.toFixed(4)}, ${p.gpsCoordsLng.toFixed(4)}</td>
    </tr>`).join('')

  const supplierRows = claim.supplierPayments.map((s, i) => `
    <tr style="background:${i%2===0?'#fff':'#f9fafb'}">
      <td style="padding:5px 10px">${s.supplierName}</td>
      <td style="padding:5px 10px;font-family:monospace;font-size:9pt">${s.poRef}</td>
      <td style="padding:5px 8px;text-align:right">${fmtAUD(s.amount)}</td>
      <td style="padding:5px 8px;text-align:center;font-weight:bold;color:${s.status==='PAID'?'#166534':'#b91c1c'}">${s.status}</td>
    </tr>`).join('')

  const certRows = claim.qualityCerts.map((c, i) => `
    <tr style="background:${i%2===0?'#fff':'#f9fafb'}">
      <td style="padding:5px 10px;font-size:9pt">${c.type.replace(/_/g,' ')}</td>
      <td style="padding:5px 10px">${c.title}</td>
      <td style="padding:5px 8px;font-size:9pt">${c.issuedBy}</td>
      <td style="padding:5px 8px;text-align:center;font-weight:bold;color:${c.attached?'#166534':'#b91c1c'}">${c.attached ? '✓ Attached' : '⚠ Outstanding'}</td>
    </tr>`).join('')

  const html = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8">
<title>Claim Bundle — ${claim.claimRef}</title>
<style>
*{box-sizing:border-box}body{font-family:Arial,sans-serif;font-size:10pt;color:#111;margin:16mm}
h1{font-size:22pt;font-weight:900;color:#1B3A6B;margin:0}
h2{font-size:13pt;color:#1B3A6B;border-bottom:2px solid #1B3A6B;padding-bottom:5px;margin:22px 0 10px;page-break-after:avoid}
.hdr{border-bottom:3px solid #1B3A6B;padding-bottom:12px;margin-bottom:18px;display:flex;justify-content:space-between;align-items:flex-end}
.partial-banner{background:#fef3c7;border:1.5px solid #fbbf24;border-radius:4px;padding:10px 16px;margin-bottom:14px;color:#92400e;font-weight:bold;text-align:center}
.cover-info table{width:100%;border-collapse:collapse;margin-bottom:14px}
.cover-info td{padding:6px 10px;border-bottom:1px solid #f3f4f6}.cover-info td:first-child{width:36%;font-weight:bold;color:#555;background:#f9fafb}
.toc{background:#f8f9ff;border:1px solid #c7d2fe;border-radius:6px;padding:14px 20px;margin-bottom:18px}
.toc-item{display:flex;justify-content:space-between;padding:5px 0;border-bottom:1px dotted #c7d2fe;font-size:10.5pt}
.amt-box{border:2px solid #1B3A6B;border-radius:5px;padding:12px 16px;margin:12px 0;display:flex;gap:20px}
.amt{flex:1;text-align:center}.amt-l{font-size:9pt;color:#666}.amt-v{font-size:14pt;font-weight:900;color:#1B3A6B}
table.data{width:100%;border-collapse:collapse;font-size:9.5pt;margin-bottom:14px}
th{background:#1B3A6B;color:#fff;padding:6px 10px;text-align:left}
.section{page-break-before:always}
.section:first-of-type{page-break-before:avoid}
.status-grid{display:flex;gap:10px;margin-bottom:12px}
.status-item{flex:1;border:1px solid #e5e7eb;border-radius:5px;padding:8px;text-align:center}
.footer{font-size:7.5pt;color:#aaa;text-align:center;margin-top:18px;border-top:1px solid #eee;padding-top:7px}
@media print{body{margin:12mm}}
</style></head><body>

${buildDocumentHeader('INVOICE')}
<div style="text-align:right;margin-top:-10px;margin-bottom:18px">
  <div style="font-size:16pt;font-weight:900;color:#1B3A6B">${isPartial ? 'PARTIAL CLAIM BUNDLE' : 'FULL CLAIM BUNDLE'}</div>
  <div style="font-family:monospace;font-weight:bold;color:#1B3A6B">${claim.claimRef}</div>
  <div style="font-size:9pt;color:#888">Generated: ${now}</div>
</div>

${isPartial ? '<div class="partial-banner">⚠ PARTIAL BUNDLE — One or more components are not yet complete or accepted. This bundle is for internal review only and is NOT ready for formal submission.</div>' : ''}

<div class="cover-info"><table>
  <tr><td>Project</td><td><strong>${job.projectName}</strong></td></tr>
  <tr><td>Client</td><td>${job.clientName}</td></tr>
  <tr><td>Contract reference</td><td>${job.contractRef}</td></tr>
  <tr><td>Milestone</td><td>${milestone.title}</td></tr>
  <tr><td>Claim status</td><td><strong>${claim.status.replace(/_/g,' ')}</strong></td></tr>
  <tr><td>Prepared by</td><td>${claim.createdByName}</td></tr>
</table></div>

<div class="status-grid">
  <div class="status-item"><div style="font-weight:bold;color:${claim.componentFS==='ACCEPTED'?'#166534':'#d97706'}">${claim.componentFS}</div><div style="font-size:8.5pt;color:#888">Financial Summary</div></div>
  <div class="status-item"><div style="font-weight:bold;color:${claim.componentEW==='ACCEPTED'?'#166534':'#d97706'}">${claim.componentEW}</div><div style="font-size:8.5pt;color:#888">Evidence of Works</div></div>
  <div class="status-item"><div style="font-weight:bold;color:${claim.componentSD==='ACCEPTED'?'#166534':'#d97706'}">${claim.componentSD}</div><div style="font-size:8.5pt;color:#888">Stat Declaration</div></div>
  <div class="status-item"><div style="font-weight:bold;color:${claim.componentFD==='ACCEPTED'?'#166534':'#d97706'}">${claim.componentFD}</div><div style="font-size:8.5pt;color:#888">Formal Docs</div></div>
  <div class="status-item"><div style="font-weight:bold;color:${claim.componentQC==='ACCEPTED'?'#166534':'#d97706'}">${claim.componentQC}</div><div style="font-size:8.5pt;color:#888">Quality Certs</div></div>
</div>

<div class="toc">
  <div style="font-weight:bold;color:#1B3A6B;margin-bottom:8px">CONTENTS</div>
  <div class="toc-item"><span>1. Financial Summary</span><span>Page 1</span></div>
  <div class="toc-item"><span>2. Evidence of Works (GPS &amp; timestamped photo log)</span><span>Page 2</span></div>
  <div class="toc-item"><span>3. Statutory Declaration &amp; Supplier Payment Schedule</span><span>Page 3</span></div>
  <div class="toc-item"><span>4. Formal Claim Documentation</span><span>Page 4</span></div>
  <div class="toc-item"><span>5. Quality Certifications</span><span>Page 5</span></div>
</div>

<div class="section">
<h2>1 — Financial Summary</h2>
<div class="amt-box">
  <div class="amt"><div class="amt-l">Milestone value</div><div class="amt-v">${fmtAUD(fs.milestoneAmount)}</div></div>
  <div class="amt"><div class="amt-l">Retention</div><div class="amt-v">${fmtAUD(fs.retentionHeld)}</div></div>
  <div class="amt"><div class="amt-l">Net claimed</div><div class="amt-v">${fmtAUD(fs.netClaimed)}</div></div>
  <div class="amt"><div class="amt-l">Total inc GST</div><div class="amt-v">${fmtAUD(fs.totalIncGST)}</div></div>
</div>
<table class="data"><thead><tr><th>Description</th><th>Supplier</th><th style="text-align:right">Amount</th><th style="text-align:center">Status</th></tr></thead>
<tbody>${poRows}
  <tr style="background:#f0fdf4"><td colspan="2" style="padding:5px 10px;font-weight:bold">Labour cost total</td><td style="padding:5px 8px;text-align:right;font-weight:bold">${fmtAUD(fs.labourCostTotal)}</td><td></td></tr>
  <tr style="background:#eff6ff"><td colspan="2" style="padding:5px 10px;font-weight:bold">Materials cost total</td><td style="padding:5px 8px;text-align:right;font-weight:bold">${fmtAUD(fs.materialsCostTotal)}</td><td></td></tr>
</tbody></table>
</div>

<div class="section">
<h2>2 — Evidence of Works</h2>
<p style="font-size:9.5pt;color:#555;margin-bottom:8px">${claim.evidencePhotos.filter(p=>p.included).length} GPS-tagged, timestamped photographs included as evidence of works completed against linked Work Instructions.</p>
<table class="data"><thead><tr><th>WI ref</th><th>Step</th><th>Crew</th><th style="text-align:center">Timestamp</th><th>GPS coordinates</th></tr></thead>
<tbody>${photoRows || '<tr><td colspan="5" style="padding:10px;text-align:center;color:#9ca3af">No photos included</td></tr>'}</tbody></table>
</div>

<div class="section">
<h2>3 — Statutory Declaration &amp; Supplier Payment Schedule</h2>
${claim.declarationSignedBy
  ? `<p style="font-size:10pt;color:#166534;font-weight:bold">✓ Signed by ${claim.declarationSignedBy} on ${fmtDT(claim.declarationSignedAt)}</p>`
  : '<p style="font-size:10pt;color:#b91c1c;font-weight:bold">⚠ Not yet signed — see attached Statutory Declaration template</p>'}
<table class="data"><thead><tr><th>Supplier / subcontractor</th><th>PO ref</th><th style="text-align:right">Amount</th><th style="text-align:center">Status</th></tr></thead>
<tbody>${supplierRows}</tbody></table>
</div>

<div class="section">
<h2>4 — Formal Claim Documentation</h2>
<p style="font-size:9.5pt;color:#555">Form type: ${job.clientType === 'TMR' ? 'TMR Milestone Claim Form (TP-MCF)' : 'SOPA QLD Payment Claim (BIF Act 2017)'}</p>
<div class="cover-info"><table>
  <tr><td>Invoice number</td><td>${fs.invoiceNumber}</td></tr>
  <tr><td>Claim reference</td><td>${claim.claimRef}</td></tr>
  <tr><td>Cumulative claimed to date</td><td>${fmtAUD(fs.previousPayments + fs.netClaimed)}</td></tr>
  <tr><td>Balance of contract remaining</td><td>${fmtAUD(fs.balanceRemaining)}</td></tr>
</table></div>
</div>

<div class="section">
<h2>5 — Quality Certifications</h2>
<table class="data"><thead><tr><th>Type</th><th>Title</th><th>Issued by</th><th style="text-align:center">Status</th></tr></thead>
<tbody>${certRows}</tbody></table>
</div>

<div class="footer">MCB Civil Electrical Pty Ltd — Claim Bundle — 5 components</div>
</body></html>`
  const w = window.open('', '_blank', 'width=1100,height=850')
  if (!w) { alert('Pop-up blocked'); return }
  w.document.write(html); w.document.close()
  setTimeout(() => { w.focus(); w.print() }, 350)
}
// ── Statutory Declaration PDF (BIF Act 2017 Qld s.99) ───────────────────────
function generateStatutoryDeclaration(
  jobNumber: string, claimRef: string, declarantName: string,
  subcontracts: typeof MOCK_SUBCONTRACTS, user: any
) {
  const now    = new Date()
  const nowStr = now.toLocaleDateString('en-AU', { day:'numeric', month:'long', year:'numeric' })
  const fmtD   = (iso: string) => new Date(iso).toLocaleDateString('en-AU', { day:'numeric', month:'short', year:'numeric' })
  const fmtAUD = (n: number) => new Intl.NumberFormat('en-AU', { style:'currency', currency:'AUD' }).format(n)

  const jobSubs = subcontracts.filter(sc => sc.jobRef === jobNumber || sc.jobId === jobNumber)
  const rows    = jobSubs.flatMap(sc =>
    sc.milestones.map(ms => ({ ...ms, subcontractorName: sc.subcontractorName, subcontractorABN: sc.subcontractorABN, reference: sc.reference }))
  )
  const totalPayable = rows.reduce((s, r) => s + r.amountPayable, 0)
  const totalPaid    = rows.filter(r => r.paid).reduce((s, r) => s + r.amountPayable, 0)
  const unpaidCount  = rows.filter(r => !r.paid && r.completedDate).length
  const allPaid      = unpaidCount === 0

  const rowHtml = rows.map((r, i) => `
    <tr style="background:${i%2===0?'#fff':'#f9fafb'}">
      <td style="padding:6px 10px">${r.subcontractorName}<br><span style="font-size:8.5pt;color:#888">ABN ${r.subcontractorABN} \u00b7 ${r.reference}</span></td>
      <td style="padding:6px 8px;font-size:9pt">${r.description.slice(0,55)}</td>
      <td style="padding:6px 8px;font-size:9pt;font-family:monospace">${r.invoiceRef ?? '\u2014'}</td>
      <td style="padding:6px 8px;text-align:right;font-family:monospace">${fmtAUD(r.amountPayable)}</td>
      <td style="padding:6px 8px;text-align:center;font-weight:bold;color:${r.paid?'#166534':'#b91c1c'}">${r.paid?`\u2713 Paid${r.paidAt?` ${fmtD(r.paidAt)}`:''}` : '\u2717 UNPAID'}</td>
    </tr>`).join('')

  const html = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8">
<title>Statutory Declaration \u2014 ${claimRef}</title>
<style>
*{box-sizing:border-box}body{font-family:Arial,sans-serif;font-size:11pt;color:#111;margin:20mm}
h1{font-size:20pt;font-weight:900;color:#1B3A6B;margin:0}
.hdr{border-bottom:3px solid #1B3A6B;padding-bottom:10px;margin-bottom:18px;display:flex;justify-content:space-between;align-items:flex-end}
.ref-box{background:#f8fafc;border:1px solid #e5e7eb;border-radius:5px;padding:12px 16px;margin-bottom:16px;display:grid;grid-template-columns:1fr 1fr;gap:8px}
.ref-box .lbl{font-size:8.5pt;color:#888;margin-bottom:2px}.ref-box .val{font-weight:600}
.statutory-box{background:#fef3c7;border:2px solid #d97706;border-radius:5px;padding:14px 18px;margin:16px 0;line-height:1.8}
.statutory-box p{margin:0 0 8px}.statutory-box p:last-child{margin:0}
table{width:100%;border-collapse:collapse;margin-bottom:12px}
th{background:#1B3A6B;color:#fff;padding:6px 10px;text-align:left;font-size:9.5pt}
.summary{display:flex;gap:10px;margin-bottom:14px}
.sum-box{flex:1;border:1px solid #e5e7eb;border-radius:5px;padding:8px;text-align:center}
.sum-v{font-size:16pt;font-weight:900}
.alert{background:#fee2e2;border:1.5px solid #fca5a5;border-radius:5px;padding:10px 14px;color:#991b1b;font-weight:bold;margin:12px 0}
.sig-row{display:flex;gap:30px;margin-top:20px}
.sig-block{flex:1}.sig-line{border-bottom:1.5px solid #333;height:30px;margin:18px 0 4px}.sig-lbl{font-size:8pt;color:#888}
.witness-box{border:1px solid #e5e7eb;border-radius:5px;padding:12px 14px;margin-top:14px}
.footer{font-size:7.5pt;color:#aaa;text-align:center;margin-top:20px;border-top:1px solid #eee;padding-top:7px}
@media print{body{margin:14mm}}
</style></head><body>
<div class="hdr">
  <div><h1>MCB Civil Electrical Pty Ltd</h1><div style="font-size:9pt;color:#888">ABN 44 128 993 087 \u00b7 14 Commerce Dr, Salisbury QLD 4107</div></div>
  <div style="text-align:right">
    <div style="font-size:14pt;font-weight:900;color:#1B3A6B">STATUTORY DECLARATION</div>
    <div style="font-size:9pt;color:#888">Building Industry Fairness (Security of Payment) Act 2017 (Qld) s.99</div>
    <div style="font-size:9pt;color:#888">${nowStr}</div>
  </div>
</div>
<div class="ref-box">
  <div><div class="lbl">Job / project</div><div class="val">${jobNumber}</div></div>
  <div><div class="lbl">Progress claim reference</div><div class="val">${claimRef}</div></div>
  <div><div class="lbl">Declarant</div><div class="val">${declarantName}</div></div>
  <div><div class="lbl">Date of declaration</div><div class="val">${nowStr}</div></div>
</div>
${!allPaid?`<div class="alert">\u26a0 WARNING: ${unpaidCount} subcontractor payment${unpaidCount!==1?'s':''} are UNPAID. Under BIF Act s.99, you must not submit a payment claim until all previous subcontractor payments have been made.</div>`:''}
<div class="statutory-box">
  <p><strong>I, ${declarantName}, Project Manager, MCB Civil Electrical Pty Ltd (ABN 44 128 993 087)</strong>, do solemnly and sincerely declare that:</p>
  <p>1. I am authorised to make this declaration on behalf of MCB Civil Electrical Pty Ltd as head contractor for the above project.</p>
  <p>2. In relation to all previous payment claims made for this project, all amounts owing to each subcontractor and supplier engaged on works included in those previous claims have been paid in full, <strong>except as noted in the payment schedule below</strong>.</p>
  <p>3. All retention withheld from subcontractors is held and will be released in accordance with the BIF Act 2017 (Qld) and the respective subcontract conditions.</p>
  <p>4. This declaration is made under the Building Industry Fairness (Security of Payment) Act 2017 (Qld), s.99. I understand a person who makes a false declaration is guilty of an offence under the Oaths Act 1867 (Qld).</p>
</div>
<div class="summary">
  <div class="sum-box"><div class="sum-v" style="color:#1B3A6B">${rows.length}</div><div style="font-size:9pt;color:#6b7280">Milestones</div></div>
  <div class="sum-box"><div class="sum-v" style="color:#166534">${rows.filter(r=>r.paid).length}</div><div style="font-size:9pt;color:#6b7280">Paid</div></div>
  <div class="sum-box"><div class="sum-v" style="color:${unpaidCount>0?'#b91c1c':'#166534'}">${unpaidCount}</div><div style="font-size:9pt;color:#6b7280">Outstanding</div></div>
  <div class="sum-box"><div class="sum-v" style="color:#1B3A6B;font-size:13pt">${fmtAUD(totalPaid)}</div><div style="font-size:9pt;color:#6b7280">Total paid</div></div>
</div>
<h2 style="font-size:11pt;color:#1B3A6B;border-bottom:1.5px solid #1B3A6B;padding-bottom:3px;margin:0 0 8px">Subcontractor Payment Schedule</h2>
<table><thead><tr><th>Subcontractor / ABN</th><th>Milestone</th><th>Invoice ref</th><th style="text-align:right">Amount payable</th><th style="text-align:center">Payment status</th></tr></thead>
<tbody>${rowHtml}</tbody>
<tfoot><tr style="background:#1B3A6B;color:#fff;font-weight:bold"><td colspan="3" style="padding:7px 10px">TOTAL</td><td style="padding:7px 8px;text-align:right;font-family:monospace">${fmtAUD(totalPayable)}</td><td></td></tr></tfoot></table>
<div class="sig-row">
  <div class="sig-block"><div class="sig-line"></div><div class="sig-lbl">Declarant signature \u2014 ${declarantName}, Project Manager, MCB Civil Electrical Pty Ltd</div></div>
  <div class="sig-block"><div class="sig-line"></div><div class="sig-lbl">Date</div></div>
</div>
<div class="witness-box">
  <div style="font-size:10pt;font-weight:bold;color:#1B3A6B;margin-bottom:8px">Witness (Justice of the Peace or Commissioner for Declarations)</div>
  <p style="font-size:9.5pt">I, ________________________________, JP/Cdec No. _______________, certify that the above named declarant personally appeared before me and made this declaration in my presence, and I verily believe the declarant understands its contents.</p>
  <div class="sig-row" style="margin-top:12px">
    <div class="sig-block"><div class="sig-line"></div><div class="sig-lbl">Witness signature</div></div>
    <div class="sig-block"><div class="sig-line"></div><div class="sig-lbl">Date</div></div>
    <div class="sig-block"><div class="sig-line"></div><div class="sig-lbl">JP/Cdec number</div></div>
  </div>
</div>
<div class="footer">MCB Civil Electrical Pty Ltd \u00b7 Statutory Declaration \u00b7 BIF Act 2017 (Qld) s.99 \u00b7 ${claimRef} \u00b7 ${nowStr}<br>
This declaration must be witnessed by a JP or Cdec. Retain the original with project financial records.</div>
</body></html>`

  const w = window.open('', '_blank', 'width=1000,height=850')
  if (!w) { alert('Pop-up blocked'); return }
  w.document.write(html); w.document.close()
  setTimeout(() => { w.focus(); w.print() }, 350)
}

// ── Subcontractor Payment Register tab (BIF Act s.99 compliance) ─────────────
function PaymentRegisterTab({ scopedJobIds, canSign }: { scopedJobIds: string[] | null; canSign: boolean }) {
  const { user } = useAuthStore()
  const jobs = scopedJobIds ? CLAIM_JOBS.filter(j => scopedJobIds.includes(j.id)) : CLAIM_JOBS
  const [selectedJobId, setSelectedJobId] = useState<string>(jobs[0]?.id ?? '')
  const activeJob = jobs.find(j => j.id === selectedJobId) ?? jobs[0]

  const jobSubs   = MOCK_SUBCONTRACTS.filter(sc => sc.jobId === activeJob?.id || sc.jobRef === activeJob?.jobNumber)
  const payRows   = jobSubs.flatMap(sc =>
    sc.milestones.map(ms => ({ ...ms, subcontractorName: sc.subcontractorName, subcontractorABN: sc.subcontractorABN, reference: sc.reference, bcipaStatus: sc.bcipaStatus }))
  )
  const totalPayable  = payRows.reduce((s, r) => s + r.amountPayable, 0)
  const totalPaid     = payRows.filter(r => r.paid).reduce((s, r) => s + r.amountPayable, 0)
  const unpaidDue     = payRows.filter(r => !r.paid && r.completedDate).length
  const bifActReady   = unpaidDue === 0
  const bcipaIssues   = jobSubs.filter(sc => ['CLAIM_DUE','RESPONSE_OVERDUE'].includes(sc.bcipaStatus))

  return (
    <div className="space-y-5">
      {/* BIF Act compliance banner */}
      <div className={`rounded-xl border p-4 ${bifActReady ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
        <div className="flex items-start gap-3">
          {bifActReady
            ? <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            : <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />}
          <div className="flex-1">
            <div className={`font-semibold text-sm ${bifActReady ? 'text-green-800' : 'text-red-800'}`}>
              BIF Act 2017 (Qld) s.99 \u2014 Declaration clearance: {bifActReady ? 'READY' : 'BLOCKED'}
            </div>
            <p className={`text-xs mt-1 ${bifActReady ? 'text-green-700' : 'text-red-700'}`}>
              {bifActReady
                ? 'All previous subcontractor payments confirmed paid. A statutory declaration may be generated and signed before submitting your next progress claim to the principal.'
                : `${unpaidDue} subcontractor payment${unpaidDue !== 1 ? 's' : ''} outstanding. Under BIF Act s.99, the head contractor must not submit a payment claim until all subcontractors have been paid for work covered by previous claims.`}
            </p>
          </div>
          {canSign && (
            <button
              onClick={() => generateStatutoryDeclaration(
                activeJob?.jobNumber ?? '', `CLAIM-${activeJob?.jobNumber ?? ''}-${new Date().toISOString().slice(0,10)}`,
                user?.fullName ?? 'Project Manager', MOCK_SUBCONTRACTS, user
              )}
              className="flex items-center gap-1.5 text-xs bg-[#1F3864] text-white px-3 py-1.5 rounded-lg font-medium hover:bg-[#2E75B6] flex-shrink-0"
            >
              <FileText className="w-3.5 h-3.5" />Generate declaration
            </button>
          )}
        </div>
      </div>

      {/* Job selector */}
      {jobs.length > 1 && (
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-xs text-gray-500 font-medium">Job:</span>
          {jobs.map(j => (
            <button key={j.id} onClick={() => setSelectedJobId(j.id)}
              className={`text-xs px-3 py-1.5 rounded-lg border font-medium transition-colors ${
                selectedJobId === j.id ? 'bg-brand-navy text-white border-brand-navy' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
              }`}>{j.jobNumber}</button>
          ))}
        </div>
      )}

      {/* BCIPA alerts */}
      {bcipaIssues.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            <span className="text-sm font-semibold text-amber-800">BIF Act payment schedule obligations</span>
          </div>
          {bcipaIssues.map(sc => (
            <div key={sc.id} className="text-xs text-amber-700 flex items-center gap-2 mt-1">
              <span className="font-medium">{sc.subcontractorName}</span>
              <span>\u2014 {BCIPA_STATUS_META[sc.bcipaStatus].label}</span>
              {sc.nextClaimDueDate && <span>\u00b7 Due {new Date(sc.nextClaimDueDate).toLocaleDateString('en-AU')}</span>}
            </div>
          ))}
        </div>
      )}

      {/* Payment register table */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-5 py-3.5 border-b bg-gray-50 flex items-center justify-between">
          <h3 className="font-semibold text-gray-900 text-sm">Payment register \u2014 {activeJob?.jobNumber}</h3>
          <div className="flex items-center gap-3 text-xs">
            <span className="text-gray-500">{payRows.filter(r => r.paid).length}/{payRows.length} paid</span>
            <span className={`font-semibold ${bifActReady ? 'text-green-700' : 'text-red-600'}`}>
              {fmtCurrency(totalPaid)} / {fmtCurrency(totalPayable)}
            </span>
          </div>
        </div>
        {payRows.length === 0 ? (
          <div className="py-14 text-center text-gray-400">
            <Building className="w-8 h-8 mx-auto mb-2 text-gray-300" />
            <p className="text-sm">No subcontract milestones for {activeJob?.jobNumber}</p>
            <p className="text-xs mt-1">Manage subcontracts in the Subcontracts module</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-500 bg-gray-50 border-b">
                  <th className="text-left px-4 py-3 font-medium">Subcontractor</th>
                  <th className="text-left px-3 py-3 font-medium">Milestone</th>
                  <th className="text-left px-3 py-3 font-medium">Invoice</th>
                  <th className="text-right px-3 py-3 font-medium">Claim</th>
                  <th className="text-right px-3 py-3 font-medium">Retention</th>
                  <th className="text-right px-3 py-3 font-medium">Payable</th>
                  <th className="text-center px-3 py-3 font-medium">Status</th>
                  <th className="text-center px-4 py-3 font-medium">Paid date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {payRows.map((r, i) => (
                  <tr key={i} className={`${!r.paid && r.completedDate ? 'bg-red-50/40' : 'hover:bg-gray-50'}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900 text-sm">{r.subcontractorName}</div>
                      <div className="text-xs text-gray-400">{r.reference}</div>
                    </td>
                    <td className="px-3 py-3 max-w-[200px]">
                      <div className="text-xs text-gray-700 truncate" title={r.description}>{r.description}</div>
                      {r.completedDate && <div className="text-xs text-gray-400 mt-0.5">Completed {fmtDate(r.completedDate)}</div>}
                    </td>
                    <td className="px-3 py-3 text-xs font-mono text-gray-600">{r.invoiceRef ?? '\u2014'}</td>
                    <td className="px-3 py-3 text-right tabular-nums text-sm">{fmtCurrency(r.claimAmount)}</td>
                    <td className="px-3 py-3 text-right tabular-nums text-xs text-gray-500">{fmtCurrency(r.retentionWithheld)}</td>
                    <td className="px-3 py-3 text-right tabular-nums font-semibold">{fmtCurrency(r.amountPayable)}</td>
                    <td className="px-3 py-3 text-center">
                      {r.paid ? (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium flex items-center gap-1 justify-center w-fit mx-auto">
                          <CheckCircle className="w-3 h-3" />Paid
                        </span>
                      ) : r.completedDate ? (
                        <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-bold flex items-center gap-1 justify-center w-fit mx-auto">
                          <AlertTriangle className="w-3 h-3" />Unpaid
                        </span>
                      ) : (
                        <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Not due</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center text-xs text-gray-500">{r.paidAt ? fmtDate(r.paidAt) : '\u2014'}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t-2 border-gray-200 bg-gray-50">
                <tr>
                  <td colSpan={3} className="px-4 py-3 text-sm font-semibold text-gray-700">Total</td>
                  <td className="px-3 py-3 text-right tabular-nums font-semibold">{fmtCurrency(payRows.reduce((s,r) => s+r.claimAmount, 0))}</td>
                  <td className="px-3 py-3 text-right tabular-nums text-xs text-gray-500">{fmtCurrency(payRows.reduce((s,r) => s+r.retentionWithheld, 0))}</td>
                  <td className="px-3 py-3 text-right tabular-nums font-bold text-gray-900">{fmtCurrency(totalPayable)}</td>
                  <td colSpan={2} className="px-3 py-3 text-center text-xs font-semibold text-green-700">{fmtCurrency(totalPaid)} confirmed paid</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-xs text-blue-800">
        <strong>BIF Act 2017 (Qld) \u2014 s.99 Statutory Declaration requirement:</strong> Before submitting a progress claim to the principal, the head contractor must provide a statutory declaration confirming all subcontractors have been paid for work covered in previous claims. The declaration must be witnessed by a Justice of the Peace or Commissioner for Declarations and submitted with the payment claim.
      </div>
    </div>
  )
}

export function ProgressPaymentClaims() {
  const { isTier, user } = useAuthStore()
  const [tab, setTab] = useState<'milestones' | 'claims' | 'payment-register'>('milestones')
  // Phase 2: live state so milestone status updates are reactive
  const [liveMilestones, setLiveMilestones] = useState<Milestone[]>(() => getLiveMilestones())
  const [liveClaims, setLiveClaims] = useState<ProgressClaim[]>(() => getLiveClaims())
  const [eligibilityCache, setEligibilityCache] = useState<Record<string, MilestoneEligibilityResult>>({})

  const isAdmin = isTier('A_DEVELOPER')
  const isPM    = isTier('B_PM')
  const isPC    = isTier('B_PC')

  const canInitiateClaim  = isAdmin || isPM || isPC
  const canSign           = isAdmin || isPM
  const canSubmit         = isAdmin || isPM
  const canRecordPayment  = isAdmin || isPM

  const scopedJobIds = user?.tier === 'B_PC' ? (user.assignedJobs ?? null) : null

  // Compute eligibility for all non-paid milestones and persist result
  const recomputeEligibility = useCallback(() => {
    const cache: Record<string, MilestoneEligibilityResult> = {}
    const updated = liveMilestones.map(ms => {
      if (['PAID', 'PAID_PARTIAL'].includes(ms.status)) return ms
      const result = computeMilestoneEligibility({
        linkedWIs: ms.linkedWIs,
        linkedITP: ms.linkedITP,
        linkedPOs: ms.linkedPOs,
        linkedDrawingTaskIds: ms.linkedDrawingTaskIds,
        wiIdMap: ms.wiIdMap,
      })
      cache[ms.id] = result
      const newStatus = result.eligible ? 'ELIGIBLE' : ms.status === 'ELIGIBLE' ? 'IN_PROGRESS' : ms.status
      if (newStatus !== ms.status) {
        updateMilestone(ms.id, { status: newStatus as any, blockingItems: result.blockingReasons.map(r => ({ type: 'WI' as any, ref: r.reference, description: r.description, resolved: false })) })
        return { ...ms, status: newStatus }
      }
      return ms
    })
    setEligibilityCache(cache)
    setLiveMilestones(updated as any)
  }, [liveMilestones])

  useEffect(() => { recomputeEligibility() }, [])

  // Platform-wide KPIs
  const allJobs = scopedJobIds ? CLAIM_JOBS.filter(j => scopedJobIds.includes(j.id)) : CLAIM_JOBS
  const totalContractValue = allJobs.reduce((s, j) => s + j.contractValue, 0)
  const totalPaid          = liveClaims.filter(c => c.status === 'PAID' && (!scopedJobIds || scopedJobIds.includes(c.jobId))).reduce((s, c) => s + (c.paidAmount ?? 0), 0)
  const activeClaims       = liveClaims.filter(c => !['PAID','VOID'].includes(c.status) && (!scopedJobIds || scopedJobIds.includes(c.jobId)))
  const eligibleCount      = liveMilestones.filter(m => m.status === 'ELIGIBLE' && (!scopedJobIds || scopedJobIds.includes(m.jobId))).length
  const actionCount        = activeClaims.filter(c => ['IN_REVIEW','PENDING_DECLARATION','APPROVED','DISPUTED'].includes(c.status) || hasFlaggedComponent(c)).length

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-[#2E75B6]" />
            Progress Payment Claims
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Milestone-linked claim packages · financial summary · evidence · statutory declarations · formal docs · quality certifications
          </p>
        </div>
        {actionCount > 0 && (
          <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5 text-sm text-amber-800 font-medium">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            {actionCount} claim{actionCount !== 1 ? 's' : ''} need attention
          </div>
        )}
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: 'Total contract portfolio', value: fmtCurrency(totalContractValue), sub: `${allJobs.length} active jobs`, icon: TrendingUp, color: 'text-[#1F3864]' },
          { label: 'Total paid to date', value: fmtCurrency(totalPaid), sub: 'all milestones', icon: CheckCircle, color: 'text-green-700' },
          { label: 'Active claim packages', value: activeClaims.length.toString(), sub: 'in progress', icon: FileSearch, color: 'text-[#2E75B6]' },
          { label: 'Milestones eligible to claim', value: eligibleCount.toString(), sub: eligibleCount > 0 ? 'action recommended' : 'none at this time', icon: Star, color: eligibleCount > 0 ? 'text-green-700' : 'text-gray-400' },
        ].map(({ label, value, sub, icon: Icon, color }) => (
          <div key={label} className="bg-white border border-gray-200 rounded-xl px-4 py-3.5">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className={`text-xl font-bold ${color}`}>{value}</div>
                <div className="text-xs font-medium text-gray-700 mt-0.5">{label}</div>
                <div className="text-xs text-gray-400">{sub}</div>
              </div>
              <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                <Icon className={`w-4 h-4 ${color}`} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Tab strip */}
      <div className="flex border-b border-gray-200">
        {([
          { key: 'milestones', label: 'Milestones', icon: Package, count: MOCK_MILESTONES.filter(m => !scopedJobIds || scopedJobIds.includes(m.jobId)).length, badge: undefined },
          { key: 'claims',            label: 'Claim Packages',      icon: FileText, count: activeClaims.length, badge: actionCount },
          { key: 'payment-register', label: 'Payment Register',     icon: Shield,   count: MOCK_SUBCONTRACTS.filter(sc => !scopedJobIds || scopedJobIds.includes(sc.jobId)).length, badge: MOCK_SUBCONTRACTS.filter(sc => sc.milestones.some(m => !m.paid && m.completedDate) && (!scopedJobIds || scopedJobIds.includes(sc.jobId))).length || undefined },
        ] as const).map(({ key, label, icon: Icon, count, badge }) => (
          <button key={key} onClick={() => setTab(key)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === key ? 'border-[#2E75B6] text-[#2E75B6]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>
            <Icon className="w-4 h-4" />
            {label}
            <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${tab === key ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>{count}</span>
            {badge && badge > 0 && (
              <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-bold">{badge} action</span>
            )}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === 'milestones' && (
        <MilestonesTab scopedJobIds={scopedJobIds} canInitiateClaim={canInitiateClaim} />
      )}
      {tab === 'claims' && (
        <ActiveClaimsTab scopedJobIds={scopedJobIds} canSign={canSign} canSubmit={canSubmit} canRecordPayment={canRecordPayment} />
      )}
      {tab === 'payment-register' && (
        <PaymentRegisterTab scopedJobIds={scopedJobIds} canSign={canSign} />
      )}
    </div>
  )
}
